import { useContext, useEffect, useRef, useState } from 'react';

import di from '../../../di/DependencyInjection';
import { GetAISummaryByRecordingUseCase, GetAISummaryByRecordingUseCaseName } from '../../../domain/use_cases/ai/GetAISummaryByRecordingUseCase';
import { GetNewAIEvaluationUseCase, GetNewAIEvaluationUseCaseName } from '../../../domain/use_cases/ai/GetNewAIEvaluationUseCase';
import GraphApi from '../../../data/settings/GraphApi';
import { useLocation, useParams } from 'react-router-dom';
import { Container, ContentLayout, ExpandableSection, Grid, Header, SpaceBetween } from '@cloudscape-design/components';

import { Select } from './components/Select';
import { ValueWithLabel } from './components/ValueWithLabel';
import { isRight } from 'fp-ts/lib/Either';
import { TranscriptSegment } from './components/TranscriptSegment';
import { TranscriptOverlay } from './components/TranscriptOverlay';
import { Entities } from './components/Entities';
import { ListItems } from './components/ListItems';
import { range } from './util';
import { LoudnessChart } from './components/LoudnessChart';
import { Tag } from './components/Tag';
import { SentimentChart } from './components/SentimentChart';
import { SpeakerTimeChart } from './components/SpeakerTimeChart';
import { Formatter } from './components/format';
import { Sentiment } from './components/Sentiment';
import { UpdateAISummaryByRecordingUseCase, UpdateAISummaryByRecordingUseCaseName } from '../../../domain/use_cases/ai/UpdateAISummaryByRecordingUseCase';
import ButtonComponent from '../../components/button/ButtonComponent';
import { ButtonType } from '../../components/button/ButtonComponentProps';
import Icons from '../../assets/Icons';
import UserContext from '../../../domain/providers/user/UserContext';
import UserContextType from '../../../domain/providers/user/UserContextType';
import ModalsContext from '../../../domain/providers/modal/ModalsContext';
import ModalsContextType from '../../../domain/providers/modal/ModalsContextType';
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent';
import { CSVLink } from "react-csv";

import "./AiPage.scss";
import { UpdateAISummaryInDisputeByRecordingUseCase, UpdateAISummaryInDisputeByRecordingUseCaseName } from '../../../domain/use_cases/ai/UpdateAISummaryInDisputeByRecordingUseCase';
import { GetAISummaryInDisputeCommentsByRecordingUseCase, GetAISummaryInDisputeCommentsByRecordingUseCaseName } from '../../../domain/use_cases/ai/GetAISummaryInDisputeCommentsByRecordingUseCase';
import { UpdateAISummaryInDisputeCommentsByRecordingUseCase, UpdateAISummaryInDisputeCommentsByRecordingUseCaseName } from '../../../domain/use_cases/ai/UpdateAISummaryInDisputeCommentsByRecordingUseCase';
import { UpdateAISwapByRecordingUseCase, UpdateAISwapByRecordingUseCaseName } from '../../../domain/use_cases/ai/UpdateAISwapByRecordingUseCase';
import { GetAIMomentsOptionsUseCase, GetAIMomentsOptionsUseCaseName } from '../../../domain/use_cases/ai/GetAIMomentsOptionsUseCase';
import UploadChannelModal from './components/UploadChannelModal';


const salesIdsLoonie = [
    419, 420, 421, 422, 423, 424, 426, 427, 428, 429, 430, 431,
    432, 433, 434, 435, 436, 437, 438, 439, 440, 441, 442, 443,
    444, 445, 446, 447, 448, 449, 450, 451, 452
]
const collectionsIdsLoonie = [
    389, 390, 391, 392, 393, 394, 395, 396, 397, 398,
    399, 400, 401, 402, 403, 404, 405, 406, 407, 408,
    409, 410, 411, 412, 413, 414, 385, 386, 387, 388
]
const salesIdsSpeedy = [
    204, 205, 206, 207, 208, 209, 210, 211, 212, 213,
    214, 215, 220, 221, 222, 223, 224, 225, 226, 227,
    228, 229, 230, 231, 232, 233, 234, 235, 236, 237,
    238, 239, 240
];
const collectionsIdsSpeedy = [
    216, 217, 218, 219, 241, 242, 243, 244, 245, 246,
    247, 248, 249, 250, 251, 252, 253, 254, 255, 256,
    257, 258, 259, 260, 261, 262, 263, 264, 265, 266
];

interface SpeakerTime {
    TotalTimeSecs: number;
}

const SCORES:  any = {
    '-1': 'Negative',
    '0': 'Neutral',
    '1': 'Positive'
}

const optionsToReplace: { [key: string]: string } = {
    options: 'Will you take action on this call? Please indicate',
    sec_options: 'What action was taken?',
};

function extractSummary(data: any) {
    if (data === "") {
        return ""
    }
    const regex = /<summary>\s*(.*?)\s*<\/summary>/;
    const summaries: any = [];

    data.forEach((item: any) => {
        const match = regex.exec(item.value);
        if (match) {
            summaries.push(match[1]);
        }
    });

    return summaries;
}

function extractQuestionsToObject(data: any) {
    
    const regex = /<question(\d+)>\s*(.*?)\s*<\/question\1>/g;
    const questions: any = [];
    let match;

    data.forEach((item: any) => {
        while ((match = regex.exec(item.value)) !== null) {
            questions.push({ name: `${match[2].split(":")[0]}`, response: match[2].split(":")[1] });
        }
    });

    return questions;
}



interface AiSummaryPageProps {
}

const AiSummaryPage: React.FC<AiSummaryPageProps> = () => {

    const location = useLocation();

    // Parse query parameters
    const searchParams = new URLSearchParams(location.search);
    const timestamp = searchParams.get("timestamp"); // Retrieve the "timestamp" query parameter

    const { recording, format } = useParams();
    
    const { addToast, openModalCustom } = useContext(ModalsContext) as ModalsContextType;
    const audioElem = useRef<any | null>(null);
    const transcriptElem = useRef<any | null>(null);
    
    const { user } = useContext(UserContext) as UserContextType;
    
    let recordURL = (recording || "") + (format ? "." + format : "");
    recordURL = !recordURL.includes(".json") ? recordURL + ".json" : recordURL;

    const [ isLoading, setIsLoading ] = useState(false);
    const [ isLoadingDispute, setIsLoadingDispute ] = useState(false);
    const [ isLoadingSwap, setIsLoadingSwap ] = useState(false);
    const [ isLoadingMoment, setIsLoadingMoment ] = useState(false);
    const [ isLoadingCommnets, setIsLoadingComments ] = useState(false);

    const [speakerLabels, setSpeakerLabels] = useState<any>({});

    const [ data, setData ] = useState<any>();
    const [ summaryFromData, setSummaryFromData ] = useState<any>("");
    const [ questionsFromData, setQuestionsFromData ] = useState<any>("");
    const [ hasInDispute, setHasInDispute ] = useState(false);

    const [loudnessData, setLoudnessData] = useState<any>({});
    const [ status, setStatus ] = useState<string []>([]);

    const [ canEditDisputeSatus, setCanEditDisputeStatus ] = useState(false);

    const [ audioData, setAudioData ] = useState<string>('');

    const [ question34, setQuestion34 ] = useState<string>("");

    const [ sumScores, setSumScores ] = useState<any>({
        manualScore: 0,
        aiScore: 0
    })

    const [ moments, setMoments ] = useState<any>({
        positive: [],
        negative: [],
        meutral: []
    })

    const [expandedSection, setExpandedSection] = useState<string | null>(null);
    const [expandedSectionLower, setExpandedSectionLower] = useState<string | null>(null);
    const [expandedMoments, setExpandedMoments] = useState<boolean>(false);
    const [expandedMomentSubsection, setExpandedMomentSubsection] = useState<string | null>(null);
    const [expandedAudioTranscription, setExpandedAudioTranscription] = useState<boolean>(false);
    const [expandedTranscriptSections, setExpandedTranscriptSections] = useState<Set<number>>(new Set());
    const [expandedQuestions, setExpandedQuestions] = useState<boolean>(false);
    const [expandedQuestionTexts, setExpandedQuestionTexts] = useState<Set<string>>(new Set()); 

    const [ aiMoments, setAiMoments ] = useState<any>([]);

    const [ evaluation, setEvaluation ] = useState<any>({
        aiEvaluated: null,
        manualEvaluated: null
    })

    const [ sumTotalEvaluationPerQuestion, setSumTotalEvaluationPerQuestion ] = useState({
        sumScore: 0,
        sumWeight: 0
    })

    const [ callStatus, setCallStatus ] = useState('');
    const [ mockApiResponse, setMockApiResponse] = useState<any>([]);
    const [formState, setFormState] = useState<{ [key: string]: string }>({});
    const [ momentsOptions, setMomentsOptions ] = useState<string>('');
    const [ momentsSec_Options, setMomentsSec_Options ] = useState<string>('');
    const [ LOBSelected, setLOBSelected ] = useState<any>('');
    const [ customerSelected, setCustomerSelected ] = useState<any>('');
    const [ agentVerifyOptions, setAgentVerifyOptions ] = useState<any>(''); 

    const _updateStatus = async (params: any) => await di.get<UpdateAISummaryInDisputeByRecordingUseCase>(UpdateAISummaryInDisputeByRecordingUseCaseName)._aiRepository.UpdateAISummaryInDisputeByRecord(recordURL as string, params);
    const _updateSwap = async (params: any) => await di.get<UpdateAISwapByRecordingUseCase>(UpdateAISwapByRecordingUseCaseName)._aiRepository.UpdateAISwapByRecord(recordURL as string, params);
    const _updateDisputeComments = async (comment: string) => await di.get<UpdateAISummaryInDisputeCommentsByRecordingUseCase>(UpdateAISummaryInDisputeCommentsByRecordingUseCaseName)._aiRepository.UpdateAISummaryInDisputeCommentsByRecord(recordURL as string, {comments: [{comment}]});

    const _getAISummary = async () => await di.get<GetAISummaryByRecordingUseCase>(GetAISummaryByRecordingUseCaseName)._aiRepository.getAISumaryByRecord(recordURL as string, 'calls');

    const _getOptions = async () => await di.get<GetAIMomentsOptionsUseCase>(GetAIMomentsOptionsUseCaseName)._aiRepository.GetAIMomentsOptions();
    
    const firstCall = async () => {
        const responseOptions = await _getOptions();
        console.log("Response from backend (moment_options):", responseOptions);
        if (isRight(responseOptions)) {
            try {
                let result = JSON.parse(responseOptions.right as string);
                const parsed = {
                    data: [Object.values(result.data)[0]]
                };
                setMockApiResponse(parsed.data[0]);
                
            } catch (error) {
                console.log(error);
            }
        }
        const response = await _getAISummary();
        if (isRight(response)) {
            let result = JSON.parse(response.right as string);
            
            const genAiSummary = (result["ConversationAnalytics"]["Summary"] ?
                Object.entries(result["ConversationAnalytics"]["Summary"]).map(([key, value]) => {
                return {
                    label: key,
                    value: (value instanceof Array ? value.join(', ') : value)
                }
                }) : []);

            const summaryExtracted = typeof result["ConversationAnalytics"]["Summary"] === 'object' ? extractSummary(genAiSummary) : result["ConversationAnalytics"]["Summary"]
            let questionsObject = extractQuestionsToObject(genAiSummary);

            if (result["ConversationAnalytics"]["Questions"]) {
                questionsObject = result["ConversationAnalytics"]["Questions"].map((item: any) => {
                    return {
                        name: item[0],
                        response: item[1]
                    }
                })
            }
            
            setData(result);
            setSummaryFromData(summaryExtracted);

            let comments = result["ConversationAnalytics"]["ManualQuestions"];
            let scores = result["ConversationAnalytics"]["ManualEvaluations"];
            
            let aiScores = result["ConversationAnalytics"]["QuestionScoring"];

            let newQuestions = result["ConversationAnalytics"]["ManualQuestions"];
            //let atlterQuestionOptions = newQuestions[34]?.[2]?.split(", ");
            setAgentVerifyOptions(newQuestions[34]?.[3] || "");
            setQuestion34(newQuestions[34]?.[0].replaceAll(" ", "").toLocaleLowerCase() || "")

            setCanEditDisputeStatus(result["StatusInfo"]["EditStatus"]);

            let commentsLong = comments.length;
            let hasInDisputeStatus = result["questionStatus"] ? true : false;
            
            setHasInDispute(hasInDisputeStatus);
            
            const aiStatus = result["questionStatus"] || new Array(commentsLong).fill(null).map(() => ({
                id: '',
                in_dispute: false
            }));

            const callStatus = result["CallStatus"] || '';
            
            setCallStatus(callStatus);
            const transitions = result["StatusInfo"]["AllowedTransitions"];
            
            const filteredData = transitions.filter((transition: any) => transition[0] === callStatus);

            const uniqueValues = Array.from(new Set(filteredData.flat().filter((value: string) => value !== callStatus))) as string[];
            
            const mediaFileData = result["ConversationAnalytics"]["SourceInformation"][0]["TranscribeJobInfo"]["MediaFileUri"];
            setAudioData(mediaFileData);
            
            setStatus(uniqueValues);

            const commentsKeys = comments.map((q: any) => {
                return {
                    question: q[0].replaceAll(" ", "").toLocaleLowerCase(),
                    comment: q[1]
                }
            });

            const statusKeys = aiStatus.map((q: any) => {
                return {
                    id: q.id,
                    in_dispute: q.in_dispute
                }
            });
            
            const scoresKeys = scores.map((q: any) => {
                return {
                    question: q[0].replaceAll(" ", "").toLocaleLowerCase(),
                    score: q[1] === 0 ? 'no' : (q[1] === 10 ? 'yes': (q[1] === 5 ? 'partial' : result["ConversationAnalytics"]["ManualEvaluated"] ==='True' ? 'notApplicable' : '')),
                    score2: q[2],
                    weight: q[2],
                    partial: q[4] === undefined ? false : q[4],
                    autofail: q[5] === undefined ? false : q[5],
                    realScore: q[5] ? 'autofail' : ''
                }
            });
            
            const aiScoresKeys = aiScores.map((q: any) => {
                return {
                    question: q[0].replaceAll(" ", "").toLocaleLowerCase(),
                    score: q[1],
                    weight: q[2],
                    questionId: q[3]
                }
            });
            
            const questionScores = questionsObject.map((q: any) => {
                return {
                    question: q.name.replaceAll(" ", "").toLocaleLowerCase(),
                    name: q.name,
                    response: q.response
                }
            });

            const dataToRender: any = [];
            scoresKeys.forEach((question: any) => {
                let newData = {
                    score: scoresKeys.filter((score: any) => score.question === question.question)[0]?.score,
                    realScore: scoresKeys.filter((score: any) => score.question === question.question)[0]?.realScore,
                    comment: commentsKeys.filter((score: any) => score.question === question.question)[0].comment,
                    question: questionScores.filter((q: any) => q.question === question.question)[0].name,
                    response: questionScores.filter((q: any) => q.question === question.question)[0].response,
                    id: questionScores.filter((q: any) => q.question === question.question)[0].question,
                    questionId: aiScoresKeys.filter((score: any) => score.question === question.question)[0].questionId,
                    status: statusKeys.filter((item: any) => item.id + '' === aiScoresKeys.filter((score: any) => score.question === question.question)[0].questionId + '').length > 0 ? statusKeys.filter((item: any) => item.id + '' === aiScoresKeys.filter((score: any) => score.question === question.question)[0].questionId + '')[0].in_dispute : false,
                    score2: scoresKeys.filter((score: any) => score.question === question.question)[0].score2,
                    weight: scoresKeys.filter((score: any) => score.question === question.question)[0].weight,
                    aiScore: aiScoresKeys.filter((score: any) => score.question === question.question)[0].score,
                    aiWeight: aiScoresKeys.filter((score: any) => score.question === question.question)[0].weight,
                    isManualSelected: (result["ConversationAnalytics"]["ManualEvaluated"] === 'True' ? (scoresKeys.filter((score: any) => score.question === question.question)[0]?.score === null ? true : false) : false),
                    partial:  scoresKeys.filter((score: any) => score.question === question.question)[0]?.partial,
                    autofail:  scoresKeys.filter((score: any) => score.question === question.question)[0]?.autofail,
                    scoresQ: scoresKeys.filter((score: any) => score.question === question.question)[0].weight * (scoresKeys.filter((score: any) => score.question === question.question)[0]?.score === 'partial' ? 5: (scoresKeys.filter((score: any) => score.question === question.question)[0]?.score === 'yes' ? 10 : 0)) / 10
                }
                dataToRender.push(newData);
            });

            let sumTotalScores = 0;
            dataToRender.map((item: any) => {
                if (item.score !== 'notApplicable' && item.score !== '' && item.realScore !== 'autofail') {
                    sumTotalScores = sumTotalScores + item.scoresQ;
                }
                
            })
            let sumTotalWeights = 0;
            dataToRender.map((item: any) => {
                if (item.score !== 'notApplicable' && item.score !== '' && item.realScore !== 'autofail') {
                    sumTotalWeights = sumTotalWeights + item.weight;
                }
            })

            setSumTotalEvaluationPerQuestion({
                sumScore: sumTotalScores,
                sumWeight: sumTotalWeights
            })
            
            let actualLOB =  result["ConversationAnalytics"]["LOB"];
            let actualCustomer = result["ConversationAnalytics"]["Cust"];
            setLOBSelected(actualLOB);
            setCustomerSelected(actualCustomer);

            if (actualCustomer) {
                const customer = actualCustomer.toLowerCase();
                const lob = actualLOB ? actualLOB.toLowerCase() : '';

                if (customer === 'loonie' || customer === 'speedy') {
                    let orderArray: number[] | null = null;

                    if (customer === 'loonie') {
                    if (lob === 'sales') {
                        orderArray = salesIdsLoonie;
                    } else if (lob === 'collection' || lob === 'collections') {
                        orderArray = collectionsIdsLoonie;
                    }
                    } else if (customer === 'speedy') {
                    if (lob === 'sales') {
                        orderArray = salesIdsSpeedy;
                    } else if (lob === 'collection' || lob === 'collections') {
                        orderArray = collectionsIdsSpeedy;
                    }
                    }

                    if (orderArray) {
                    dataToRender.sort((a: any, b: any) => orderArray!.indexOf(a.questionId) - orderArray!.indexOf(b.questionId));
                    } else {
                    dataToRender.sort((a: any, b: any) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
                    }
                } else {
                    dataToRender.sort((a: any, b: any) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
                }
            }
            
            setQuestionsFromData(dataToRender);

            if (result["ConversationAnalytics"]["ManualOverallScore"] === undefined) {
                let sum01 = 0;
                let sum02 = 0;
                let counterManual = 0;
                dataToRender.map((item: any) => {
                    if (item.score !== null) {
                        sum01 = sum01 + (item.weight * item.score);
                    } else {
                        counterManual = counterManual + 1;
                    }
                    if (item.weight) {
                        sum02 = sum02 + item.weight;
                    }
                    
                })
                let sum03 = 0;
                let sum04 = 0;
                let counter = 0;
                dataToRender.map((item: any) => {
                    if (item.aiScore !== null) {
                        sum03 = sum03 + (item.weight * item.aiScore);
                    } else {
                        counter = counter + 1;
                    }
                    
                    if (item.aiWeight) {
                        sum04 = sum04 + item.aiWeight;
                    }
                    
                });
    
                setSumScores({
                    manualScore: Math.floor(sum01/sum02 * 1000) / 1000,
                    aiScore: Math.floor(sum03/sum04 * 1000) / 1000
                })
    
                if (counter === dataToRender.length) {
                    setSumScores({
                        ...sumScores,
                        aiScore: 'No data'
                    })
                }
                if (counterManual === dataToRender.length) {
                    setSumScores({
                        ...sumScores,
                        manualScore: 'No data'
                    })
                }
            } else {
                setSumScores({
                    manualScore: result["ConversationAnalytics"]["ManualOverallScore"] !== null ? result["ConversationAnalytics"]["ManualOverallScore"]: 'No Data',
                    aiScore: result["ConversationAnalytics"]["AIOverallScore"] !== null ? result["ConversationAnalytics"]["AIOverallScore"] : 'No Data'
                })
            }
            

            let moments = result['ConversationAnalytics']['ManualMoments'];
            let aiMoments = result['ConversationAnalytics']['Moments'];
            let usersComments = result['Comment'];
            setComments(usersComments);

            const transformedObject = aiMoments.reduce((acc: any, item: any) => {
                acc[item[3]] = item;
                return acc;
            }, {});
            
            setAiMoments(transformedObject);

            setEvaluation({
                aiEvaluated: result['ConversationAnalytics']['AIEvaluated'],
                manualEvaluated: result['ConversationAnalytics']['ManualEvaluated']
            })

            const positiveMomentsFromData = moments
                ?.filter((item: any) => item[2] === 1).sort((a: any, b: any) => a[0].localeCompare(b[0])); 

            // Filter and map negative moments, ignoring invalid entries
            const negativeMomentsFromData = moments
                ?.filter((item: any) => item[2] === -1).sort((a: any, b: any) => a[0].localeCompare(b[0])); 

            // Filter and map neutral moments, ignoring invalid entries
            const neutralMomentsFromData = moments
                ?.filter((item: any) => item[2] === 0).sort((a: any, b: any) => a[0].localeCompare(b[0])); 

            setMoments({
                positive: positiveMomentsFromData,
                negative: negativeMomentsFromData,
                neutral: neutralMomentsFromData
            });
        }
    }

    const handleChange = (id: number, sectionOption: string, option: string) => {
        
        const momentPositiveFiltered = moments.positive?.filter((item: any) => item[3] === id);
        const momentNeutralFiltered = moments.neutral?.filter((item: any) => item[3] === id);
        const momentNegativeFiltered = moments.negative?.filter((item: any) => item[3] === id);

        if (momentPositiveFiltered.length > 0 ) {
            if(sectionOption === 'options') momentPositiveFiltered[0][6] = option;
            if(sectionOption !== 'options') momentPositiveFiltered[0][7] = option;
            
            let filterDataWithNoPositiveValue = moments.positive?.filter((item: any) => item[3] !== id);
            let newPositiveData = [...filterDataWithNoPositiveValue, ...momentPositiveFiltered];
            setMoments({
                ...moments,
                positive: newPositiveData.sort((a: any, b: any) => a[0].localeCompare(b[0]))
            });
        }
        if (momentNegativeFiltered.length > 0 ) {
            
            if(sectionOption === 'options') momentNegativeFiltered[0][6] = option;
            if(sectionOption !== 'options') momentNegativeFiltered[0][7] = option;

            let filterDataWithNoNegativeValue = moments.negative?.filter((item: any) => item[3] !== id);
            let newNegativeData = [...filterDataWithNoNegativeValue, ...momentNegativeFiltered];
            setMoments({
                ...moments,
                negative: newNegativeData.sort((a: any, b: any) => a[0].localeCompare(b[0]))
            });
        }
        if (momentNeutralFiltered.length > 0 ) {
            if(sectionOption === 'options') {momentNeutralFiltered[0][6] = option};
            if(sectionOption !== 'options') {momentNeutralFiltered[0][7] = option};
            let filterDataWithNoNeutralValue = moments.neutral?.filter((item: any) => item[3] !== id);
            let newNeutralData = [...filterDataWithNoNeutralValue, ...momentNeutralFiltered];
            setMoments({
                ...moments,
                negative: newNeutralData.sort((a: any, b: any) => a[0].localeCompare(b[0]))
            });
        }
        
        
    };

    const _handleMomentEvaluation = (value: string, element: string, id: number) => {
        
        const momentPositiveFiltered = moments.positive?.filter((item: any) => item[3] === id);
        
        if (momentPositiveFiltered.length > 0 ) {
            momentPositiveFiltered[0][1] = value === 'yes' ?  1 : 0;
            let filterDataWithNoPositiveValue = moments.positive?.filter((item: any) => item[3] !== id);
            let newPositiveData = [...filterDataWithNoPositiveValue, ...momentPositiveFiltered];
            setMoments({
                ...moments,
                positive: newPositiveData.sort((a: any, b: any) => a[0].localeCompare(b[0]))
            });
        }
        const momentNeutralFiltered = moments.neutral?.filter((item: any) => item[3] === id);
        if ( momentNeutralFiltered.length > 0) {
            momentNeutralFiltered[0][1] = (value === 'yes' ?  1 : 0);
            let filterDataWithNoNeutralValue = moments.neutral?.filter((item: any) => item[3] !== id);
            let newNeutralData = [...filterDataWithNoNeutralValue, ...momentNeutralFiltered];
            setMoments({
                ...moments,
                neutral: newNeutralData.sort((a: any, b: any) => a[0].localeCompare(b[0]))
            });
        }
        const momentNegativeFiltered = moments.negative?.filter((item: any) => item[3] === id);
        if (momentNegativeFiltered.length > 0) {
            momentNegativeFiltered[0][1] = value === 'yes' ?  1 : 0;
            let filterDataWithNoNegativeValue = moments.negative?.filter((item: any) => item[3] !== id);
            let newNegativeData = [...filterDataWithNoNegativeValue, ...momentNegativeFiltered];
            setMoments({
                ...moments,
                negative: newNegativeData.sort((a: any, b: any) => a[0].localeCompare(b[0]))
            });
        }
        
    }
    const  _handleScoreChange = (value: string, question: string) => {
        
        let filterData = questionsFromData.filter((item: any) => item.id === question);
        filterData[0]['score'] = value;

        let filterDataWithNoValue = questionsFromData.filter((item: any) => item.id !== question);
        let newData = [...filterDataWithNoValue, ...filterData];

        if (customerSelected) {
            const customer = customerSelected.toLowerCase();
            const lob = LOBSelected ? LOBSelected.toLowerCase() : '';

            if (customer === 'loonie' || customer === 'speedy') {
                let orderArray: number[] | null = null;

                if (customer === 'loonie') {
                if (lob === 'sales') {
                    orderArray = salesIdsLoonie;
                } else if (lob === 'collection' || lob === 'collections') {
                    orderArray = collectionsIdsLoonie;
                }
                } else if (customer === 'speedy') {
                if (lob === 'sales') {
                    orderArray = salesIdsSpeedy;
                } else if (lob === 'collection' || lob === 'collections') {
                    orderArray = collectionsIdsSpeedy;
                }
                }

                if (orderArray) {
                newData.sort((a, b) => orderArray!.indexOf(a.questionId) - orderArray!.indexOf(b.questionId));
                } else {
                newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
                }
            } else {
                newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
            }
            } else {
            // if no actualCustomer, just sort ascending by id
            newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
        }

        setQuestionsFromData(newData);
        
    }

    const  _handleStatusChange = (value: string, id: number) => {
        let filterData = questionsFromData.filter((item: any) => item.questionId === id);
        filterData[0]['status'] = !filterData[0]['status'];
        let filterDataWithNoValue = questionsFromData.filter((item: any) => item.questionId !== id);
        let newData = [...filterDataWithNoValue, ...filterData];
                if (customerSelected) {
            const customer = customerSelected.toLowerCase();
            const lob = LOBSelected ? LOBSelected.toLowerCase() : '';

            if (customer === 'loonie' || customer === 'speedy') {
                let orderArray: number[] | null = null;

                if (customer === 'loonie') {
                if (lob === 'sales') {
                    orderArray = salesIdsLoonie;
                } else if (lob === 'collection' || lob === 'collections') {
                    orderArray = collectionsIdsLoonie;
                }
                } else if (customer === 'speedy') {
                if (lob === 'sales') {
                    orderArray = salesIdsSpeedy;
                } else if (lob === 'collection' || lob === 'collections') {
                    orderArray = collectionsIdsSpeedy;
                }
                }

                if (orderArray) {
                newData.sort((a, b) => orderArray!.indexOf(a.questionId) - orderArray!.indexOf(b.questionId));
                } else {
                newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
                }
            } else {
                newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
            }
            } else {
            // if no actualCustomer, just sort ascending by id
            newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
        }

        setQuestionsFromData(newData);
    }
    
    const _updateStatusCall = async () => {
        setIsLoadingDispute(true);
        if (callStatus !== '') {
            let body = {
                status: callStatus,
                questions: []
            }
            
            try {
                let response = await _updateStatus(body);
                setIsLoadingDispute(false);
                if (isRight(response)) {
                    if (response.right !== "ValidationError") {
                        await firstCall();
                        addToast("The status has been stored", "success", undefined);
                    } else {
                        addToast("There was an error creating the status", "error", undefined);
                    }
                } 
            } catch (error) {
                setIsLoadingDispute(false);
            }
        }
    }
    const _updateSwapCall = async () => {
        setIsLoadingSwap(true);
            try {
                let response = await _updateSwap({});
                setIsLoadingSwap(false);
                if (isRight(response)) {
                    if (response.right !== "ValidationError") {
                        await firstCall();
                        addToast("The status has been stored", "success", undefined);
                    } else {
                        addToast("There was an error creating the status", "error", undefined);
                    }
                } 
            } catch (error) {
                setIsLoadingSwap(false);
            }
    }

    const _handleStartDispute = async () => {
        setIsLoadingDispute(true);
        let statusResult = questionsFromData.map((item: any) => {
            return {
                id: item.questionId,
                in_dispute: item.status
            }
        });
        let hasDisputeElement = statusResult.filter((item: any) => item.in_dispute !== false);
        
        if (hasDisputeElement.length > 0) {
            let body = {
                status: 'Acknowledged',
                questions: [
                    ...statusResult
                ]
            }
            
            try {
                let response = await _updateStatus(body);
                setIsLoadingDispute(false);
                if (isRight(response)) {
                    if (response.right !== "ValidationError") {
                        await firstCall();
                        addToast("The status has been stored", "success", undefined);
                    } else {
                        addToast("There was an error creating the status", "error", undefined);
                    }
                } 
            } catch (error) {
                setIsLoadingDispute(false);
            }
        } else {
            setIsLoadingDispute(false);
        }
    }

    const  _handleEvaluatedChange = (value: boolean, question: string) => {
        
        let filterData = questionsFromData.filter((item: any) => item.id === question);
        filterData[0]['isManualSelected'] = value;

        let filterDataWithNoValue = questionsFromData.filter((item: any) => item.id !== question);
        
        let newData = [...filterDataWithNoValue, ...filterData];
                if (customerSelected) {
            const customer = customerSelected.toLowerCase();
            const lob = LOBSelected ? LOBSelected.toLowerCase() : '';

            if (customer === 'loonie' || customer === 'speedy') {
                let orderArray: number[] | null = null;

                if (customer === 'loonie') {
                if (lob === 'sales') {
                    orderArray = salesIdsLoonie;
                } else if (lob === 'collection' || lob === 'collections') {
                    orderArray = collectionsIdsLoonie;
                }
                } else if (customer === 'speedy') {
                if (lob === 'sales') {
                    orderArray = salesIdsSpeedy;
                } else if (lob === 'collection' || lob === 'collections') {
                    orderArray = collectionsIdsSpeedy;
                }
                }

                if (orderArray) {
                newData.sort((a, b) => orderArray!.indexOf(a.questionId) - orderArray!.indexOf(b.questionId));
                } else {
                newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
                }
            } else {
                newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
            }
            } else {
            // if no actualCustomer, just sort ascending by id
            newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
        }

        setQuestionsFromData(newData);
        
    }

    const _handleCommentChange = (value: string, question: string) => {
        let filterData = questionsFromData.filter((item: any) => item.id === question);
        filterData[0]['comment'] = value;

        let filterDataWithNoValue =questionsFromData.filter((item: any) => item.id !== question);
        let newData = [...filterDataWithNoValue, ...filterData];
                if (customerSelected) {
            const customer = customerSelected.toLowerCase();
            const lob = LOBSelected ? LOBSelected.toLowerCase() : '';

            if (customer === 'loonie' || customer === 'speedy') {
                let orderArray: number[] | null = null;

                if (customer === 'loonie') {
                if (lob === 'sales') {
                    orderArray = salesIdsLoonie;
                } else if (lob === 'collection' || lob === 'collections') {
                    orderArray = collectionsIdsLoonie;
                }
                } else if (customer === 'speedy') {
                if (lob === 'sales') {
                    orderArray = salesIdsSpeedy;
                } else if (lob === 'collection' || lob === 'collections') {
                    orderArray = collectionsIdsSpeedy;
                }
                }

                if (orderArray) {
                newData.sort((a, b) => orderArray!.indexOf(a.questionId) - orderArray!.indexOf(b.questionId));
                } else {
                newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
                }
            } else {
                newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
            }
            } else {
            // if no actualCustomer, just sort ascending by id
            newData.sort((a, b) => (a.questionId < b.questionId ? -1 : a.questionId > b.questionId ? 1 : 0));
        }

        setQuestionsFromData(newData);
    }

    useEffect(() => {
        if (data) {
            const labels: any = data["ConversationAnalytics"]["SpeakerLabels"] || [];
            const newSpeakerLabels: any = {
                NonTalkTime: "Silence",
                Interruptions: "Interruptions",
                Positive: "Positive",
                Negative: "Negative",
                Neutral: "Neutral"
            };
            labels.map((Speaker: any) => {
                newSpeakerLabels[Speaker["Speaker"]] = Speaker["DisplayText"];
            });
            
            setSpeakerLabels(newSpeakerLabels);
        }
    }, [data]);

    const isTranscribeCallAnalyticsMode =
    data?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
        ?.TranscribeApiType === "analytics";

    const createLoudnessData = (segment: any) => {
        const start = Math.floor(segment.SegmentStartTime);
        const end = Math.floor(segment.SegmentEndTime);
        const r = range(start, end);
        return r.map((item, i) => ({
            x: item,
            y: segment.LoudnessScores[i],
            interruption: segment.SegmentInterruption && item === start ? 100 : null,
            sentiment: (segment.SentimentIsNegative ? -5 : (segment.SentimentIsPositive && segment.LoudnessScores[i] > 0 ? 5 : 0)),
            sentimentScore: segment.SentimentScore,
            silence: (segment.LoudnessScores[i] === 0 ? true : false)
        }));
    };

    const createSentimentData = (segment: any) => {
        const start = Math.floor(segment.SegmentStartTime);
        const end = Math.floor(segment.SegmentEndTime);
        const r = range(start, end);
        return r.map((item, i) => ({
            x: item,
            y: (segment.SentimentIsNegative === 1 ? segment.SentimentScore * -1 : segment.SentimentScore)
        }));
    }

    const issuesTab = () => {
        return <div key='issuesTab'>
            {data?.ConversationAnalytics?.IssuesDetected?.length > 0 ? 
            data?.ConversationAnalytics?.IssuesDetected?.map((issue: any, j: any) => (
                <Tag key={j}
                style={{
                    "--highlight-colour": "yellow",
                }}
                >
                {issue.Text}
                </Tag>
            )) : <div>No issues detected.</div>
            }
        </div>
    }
    const actionItemsTab = () => {
    return <div key='actionItemsTab'>
        {data?.ConversationAnalytics?.ActionItemsDetected?.length > 0 ? 
        data?.ConversationAnalytics?.ActionItemsDetected?.map(
        (actionItem: any, j: any) => (
            <Tag key={j}
            style={{
                "--highlight-colour": "LightPink",
            }}
            >
            {actionItem.Text}
            </Tag>
        )
        ) : <div>No action items detected.</div>
        }
    </div>
    }
    
    const outcomesTab = () => {
    return <div key='outcomesTab'>
        {data?.ConversationAnalytics?.OutcomesDetected?.length > 0 ?
        data?.ConversationAnalytics?.OutcomesDetected?.map(
        (outcome: any, j: any ) => (
            <Tag key={j}
            style={{
                "--highlight-colour": "Aquamarine",
            }}
            >
            {outcome.Text}
            </Tag>
        )
        ): <div>No outcomes detected.</div>
    }
    </div>
    }

    const getSentimentTrends = (d: any, target: any, labels: any) => {
        const id = Object.entries(labels).find(([_, v]) => v === target)?.[0];
        if (!id) return {};
        return d?.ConversationAnalytics?.SentimentTrends[id];
    };

    const hasTranscribeStreamingSession =
    data?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
        ?.StreamingSession;

    const usedCustomLanguageModel =
    data?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
        ?.CLMName;

    
    const callDetailColumn = [
        {
            label: "Timestamp",
            value: (d: any) => timestamp?.split('.')[0] ?? d?.ConversationAnalytics?.ConversationTime.substring(0, 19),
        },
        { label: "Interaction ID", value: (d: any) => d?.ConversationAnalytics?.GUID },
        { label: "Agent", value: (d: any) => d?.ConversationAnalytics?.Agent },
        { label: "Agent Result", value: (d: any) => d?.ConversationAnalytics?.AgentResult },
        { label: "HR ID", value: (d: any) => d?.ConversationAnalytics?.HRID },
        { label: "Workgroup", value: (d: any) => d?.ConversationAnalytics?.Workgroup },
        { label: "Account Number", value: (d: any) => d?.ConversationAnalytics?.AccountNumber },
        { label: "User ID", value: (d: any) => d?.ConversationAnalytics?.UserID },
        {
            label: "Call Duration",
            value: (d: any) => Formatter.Time(d.ConversationAnalytics.Duration),
        },
        {
            label: "Entity Recognizer Name",
            value: (d: any) => d?.ConversationAnalytics?.EntityRecognizerName,
        },
        {
            label: "Language Model",
            value: (d: any) =>
                usedCustomLanguageModel
                ? d?.ConversationAnalytics?.LanguageCode + " [" +
                    d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo?.CLMName + "]"
                : d?.ConversationAnalytics?.LanguageCode
        },
        {
            label: "Agent Sentiment",
            value: (d: any) => (
            <Sentiment
                score={getSentimentTrends(d, "Agent", speakerLabels)?.SentimentScore}
                trend={getSentimentTrends(d, "Agent", speakerLabels)?.SentimentChange}
            />
            ),
        },
        {
            label: "Customer Sentiment",
            value: (d: any) => (
            <Sentiment
                score={
                getSentimentTrends(d, "Customer", speakerLabels)?.SentimentScore
                }
                trend={
                getSentimentTrends(d, "Customer", speakerLabels)?.SentimentChange
                }
            />
            ),
        },
    ];

    const transcribeDetailColumn = [
        {
            label: "Type",
            value: (d: any) =>
            isTranscribeCallAnalyticsMode
                ? hasTranscribeStreamingSession
                ? "Transcribe Streaming Call Analytics"
                : "Transcribe Call Analytics"
                : hasTranscribeStreamingSession
                ? "Transcribe Streaming"
                : "Transcribe"
        },
        {
            label: "Call file name",
            value: (d: any) => (
            <div key='jobIdKey' className="text-break">
                {
                d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
                    ?.TranscriptionJobName
                }
            </div>
            ),
        },
        {
            label: "File Format",
            value: (d: any) =>
            d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
                ?.MediaFormat,
        },
        {
            label: "Sample Rate",
            value: (d: any) =>
            d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
                ?.MediaSampleRateHertz,
        },
        {
            label: "PII Redaction",
            value: (d: any) =>
            d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
                ?.RedactedTranscript === true
                ? "Enabled"
                : "Disabled"
        },
        {
            label: "Custom Vocabulary",
            value: (d: any) =>
            d?.ConversationAnalytics?.SourceInformation[0]?.TranscribeJobInfo
                ?.VocabularyName,
        },
        {
            label: "Vocabulary Filter",
            value: (d: any) =>
            d.ConversationAnalytics.SourceInformation[0]?.TranscribeJobInfo
                ?.VocabularyFilter,
        },
        {
            label: "Average Word Confidence",
            value: (d: any) =>
            Formatter.Percentage(
                d.ConversationAnalytics.SourceInformation[0]?.TranscribeJobInfo
                ?.AverageWordConfidence
            ),
        },
    ];

    const _handleUpdateMoments = async () => {
        setIsLoadingMoment(true);
        let momentsToSend = [...moments.positive, ...moments.neutral, ...moments.negative];

        let bodytoSend = {
            ManualMoments: momentsToSend,
        }
        
        try {
            const _getAISummary = async () => await di.get<UpdateAISummaryByRecordingUseCase>(UpdateAISummaryByRecordingUseCaseName)._aiRepository.updateAISumaryByRecord(recordURL, bodytoSend, 'calls');
            const response = await _getAISummary();
        

            if (isRight(response)) {
                if (response.right !== "ValidationError") {
                    await firstCall();
                    setIsLoadingMoment(false);
                    addToast("The evaluation has been stored", "success", undefined);
                } else {
                    setIsLoadingMoment(false);
                    addToast("There was an error creating the evaluation", "error", undefined);
                }
            } 
        } catch (error) {  
            setIsLoadingMoment(false);
        }
        
    }

    const _handleUpdateComments = async () => {

        //setIsLoading(true);

        let manualEvaluations = questionsFromData.map((item: any) => {
            const normalizedQuestion = item.question.replaceAll(" ", "").toLocaleLowerCase();

            if (normalizedQuestion === question34) {
                return [
                    "Did the agent fail to verify the email address, contact number or both?",
                    item.comment,
                    "Email Address, Contact Number, Contact Number & Email Address, N/A",
                    agentVerifyOptions
                ];
            }

            return [item.question, item.comment];
        });

        let manualQuestions = questionsFromData.map((item: any) => {
            return [
                item.question,
                item.score === 'notApplicable' ? null : (item.score === 'yes' ? 10 : (item.score === 'no' ? 0 : (item.score === '' ? null : (item.partial ? 5 : 0)))),
                item.score2,
                item.weight,
                item.partial,
                item.autofail

            ]
        });
        
        let bodytoSend = {
            ManualQuestions: manualEvaluations,
            ManualEvaluations: manualQuestions
        }
        
        try {
            const _getAISummary = async () => await di.get<UpdateAISummaryByRecordingUseCase>(UpdateAISummaryByRecordingUseCaseName)._aiRepository.updateAISumaryByRecord(recordURL, bodytoSend, 'calls');
            const response = await _getAISummary();
        
            setIsLoading(false);

            if (isRight(response)) {
                if (response.right !== "ValidationError") {
                    await firstCall();
                    addToast("The evaluation has been stored", "success", undefined);
                } else {
                    addToast("There was an error creating the evaluation", "error", undefined);
                }
            } 
        } catch (error) {
            setIsLoading(false);
        }
        
    }

    const _handleRequestNewAIEvaluation = async () => {

        setIsLoading(true);

        try {

            const channel = "calls";

            const _updateAIEvaluation = async () => await di.get<GetNewAIEvaluationUseCase>(GetNewAIEvaluationUseCaseName)._aiRepository.getNewAIEvaluation(`${recordURL}?channel=${channel}`);
            const response = await _updateAIEvaluation();

            if (isRight(response)) {
                // Check if response contains job_id (new format with 202 response)
                const responseData = response.right as any;
                if (responseData?.job_id) {
                    const jobId = responseData.job_id;
                    addToast("AI evaluation queued, waiting for processing...", "default", undefined);
                    
                    // Timeout: stop waiting after 5 minutes and show a message
                    const timeoutId = setTimeout(() => {
                        setIsLoading(false);
                        subscription.close();
                        addToast("Evaluation is taking longer than expected. Please refresh the page in a few minutes.", "default", 8000);
                    }, 5 * 60 * 1000);

                    // Subscribe to GraphQL subscription
                    const subscription = GraphApi.createSubscription();
                    const subscriptionQuery = `subscription OnJobUpdate($job_id: ID!) {
  onJobUpdate(job_id: $job_id) {
    job_id
    status
    result_url
    error
    channel
    call_pk
    call_sk
    user_id
    created_at
    updated_at
  }
}`;
                    
                    await subscription.subscribe({
                        subscription: subscriptionQuery,
                        variables: { job_id: jobId },
                        onNext: (data: any) => {
                            console.log('GraphQL subscription data received:', data);
                            if (data?.onJobUpdate) {
                                const jobUpdate = data.onJobUpdate;
                                console.log('Job update received:', jobUpdate);
                                
                                // Check if job is completed
                                // RagJobStatus enum: QUEUED, RUNNING, DONE, FAILED
                                if (jobUpdate.status === 'DONE') {
                                    clearTimeout(timeoutId);
                                    setIsLoading(false);
                                    subscription.close();
                                    firstCall();
                                    addToast("AI evaluation completed successfully", "success", undefined);
                                } else if (jobUpdate.status === 'FAILED' || jobUpdate.error) {
                                    clearTimeout(timeoutId);
                                    setIsLoading(false);
                                    subscription.close();
                                    firstCall();
                                    addToast(jobUpdate.error || "AI evaluation failed", "error", undefined);
                                } else {
                                    // For other statuses (QUEUED, RUNNING), keep waiting
                                    console.log(`Job status: ${jobUpdate.status}, waiting for completion...`);
                                }
                            } else {
                                console.warn('Unexpected data format from subscription:', data);
                            }
                        },
                        onError: (error: Error) => {
                            console.error('Subscription error:', error);
                            clearTimeout(timeoutId);
                            setIsLoading(false);
                            subscription.close();
                            
                            // Check for specific error about realtime channel
                            if (error.message && error.message.includes('not supported through the realtime channel')) {
                                console.warn('Subscription not enabled for realtime channel. This is a backend configuration issue.');
                                addToast("Subscription not available. Please check AppSync configuration.", "error", undefined);
                            } else {
                                addToast(`Error monitoring AI evaluation: ${error.message}`, "error", undefined);
                            }
                        },
                        onComplete: () => {
                            console.log('Subscription completed');
                            setIsLoading(false);
                        }
                    });
                } else if (response.right !== "ValidationError") {
                    // Legacy response format (stream)
                    setIsLoading(false);
                    firstCall();
                    addToast("A new AI evaluation has been created", "success", undefined);
                } else {
                    setIsLoading(false);
                    addToast("There was an error creating the evaluation", "error", undefined);
                }
            } else {
                setIsLoading(false);
                addToast("There was an error creating the evaluation", "error", undefined);
            }
        } catch (error) {
            setIsLoading(false);
            addToast("There was an error creating the evaluation", "error", undefined);
        }
        
    }

    const audioEndTimestamps = (data?.SpeechSegments || [])
    .map(({ WordConfidence }: { WordConfidence: { EndTime: number; StartTime: number }[] }) => WordConfidence)
    .flat()
    .reduce((accumulator: { EndTime: number; StartTime: number }[], item: any) => [
        ...accumulator,
        { EndTime: item.EndTime, StartTime: item.StartTime }
    ], [])
    .sort((a: any, b: any) => a.EndTime - b.EndTime);
    

    const onAudioPlayTimeUpdate = () => {
        let elementEndTime = undefined;
        
        for (let i = 0; i < audioEndTimestamps.length; i++) {
            
            if (audioElem.current.currentTime <= audioEndTimestamps[i]['EndTime'] && audioElem.current.currentTime >= audioEndTimestamps[i]['StartTime']) {
                elementEndTime = audioEndTimestamps[i]['EndTime'];
                break;
            }
        }

        [...transcriptElem.current.getElementsByClassName('playing')].map(elem => elem.classList?.remove("playing"));
        transcriptElem.current.querySelector('span[data-end="'+elementEndTime+'"]')?.classList?.add("playing");
        
    };

    const setAudioCurrentTime = (e: any) => {
        const a = document.getElementsByTagName("audio")[0];
        a.currentTime = parseFloat(e.target.dataset.start);
        a.play();
    };

    const setAudioCurrentTimeForMoment = (timeToStart: any) => {
        const a = document.getElementsByTagName("audio")[0];
        a.currentTime = parseFloat(timeToStart);
        a.play();

        const maxWaitTime = 10000; // 10 seconds
        const intervalTime = 100;
        let elapsedTime = 0;

        const waitForPlaying = setInterval(() => {
            const element = document.getElementById(timeToStart);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                clearInterval(waitForPlaying);
            } else {
                elapsedTime += intervalTime;
                if (elapsedTime >= maxWaitTime) {
                    clearInterval(waitForPlaying);
                }
            }
        }, intervalTime);
    };

    useEffect(() => {
        firstCall();
    }, []);

    
    useEffect(() => {
    const loudness: any = {};

    if (isTranscribeCallAnalyticsMode) {
      // TCA mode
        let interruptions: any[] = [];
        let silence: any[] = [];
        let positive: any[] = [];
        let negative: any[] = [];
        let neutral: any[] = [];
        let nonSilence: any[] = [];

        Object.keys(speakerLabels).forEach(key => {
            let keyLoudness = (data?.SpeechSegments || [])
            .filter((segment: any) => segment.SegmentSpeaker === key)
            .map(createLoudnessData)
            .flat();
            
            loudness[key] = keyLoudness;
            let newInterruptions = keyLoudness.filter((d: any) => d.interruption)
                .map((d: any) => ({ y: d.interruption, x: d.x }))
            interruptions = interruptions.concat(newInterruptions)

            let newSilence = keyLoudness.filter((d: any) => d.silence)
                .map((d: any) => ({ x: d.x, y: 100 }))
            silence = silence.concat(newSilence);

            keyLoudness.forEach((item: any) => {
                let sentimentItem = {
                x: item.x,
                y: 10,
                sentiment: item.sentiment
            };
            if (item.sentiment > 0) positive.push(sentimentItem)
            else if (item.sentiment < 0) negative.push(sentimentItem)
            else neutral.push(sentimentItem);
            nonSilence[item.x.toString()] = sentimentItem;
            });

        });
    
    // generate the rest of the silence
    if (data) {
        const r = range(0, parseInt(data?.ConversationAnalytics.Duration));
        r.map((item, i) => {
            if (!(i in nonSilence)) {
            silence = silence.concat({ x: i, y: 100 });
            }
        });
    }

        loudness['Interruptions'] = interruptions;
        loudness['NonTalkTime'] = silence;
        loudness['Positive'] = positive;
        loudness['Neutral'] = neutral;
        loudness['Negative'] = negative;
    } else {
      // this is transcribe standard
        Object.keys(speakerLabels).forEach(key => {
            if (key.indexOf('spk_') >= 0) {
                let keyLoudness = (data?.SpeechSegments || [])
                .filter((segment: any) => segment.SegmentSpeaker === key)
                .map(createSentimentData)
                .flat();
                loudness[key] = keyLoudness;
                }
            });
    }
        setLoudnessData(loudness);
        
    }, [speakerLabels])

    const [ csvData, setCsvData ] = useState<any>([]);

    useEffect(()=> {
        
        if (data) {
            let dataToSave = data["SpeechSegments"].map((speaker: any) => {
                let name = speaker.SegmentSpeaker;
                let nameType = data["ConversationAnalytics"]["SpeakerLabels"].find((type: any) => type.Speaker === name).DisplayText;
                let time = Formatter.Time(speaker.SegmentStartTime);
                let textToDisplay = speaker.DisplayText;
                return [nameType, time, textToDisplay];
            });
            dataToSave.unshift(['Person Type', 'Started Time', 'Dialogue']);
            setCsvData(dataToSave);
        }
        
    }, [data]);

    const [comments, setComments] = useState<any>();
    const [newComment, setNewComment] = useState("");
    const [ momentSelectedToTrack, setMomentSelectedToTrack ] = useState<string>();
    const [ momentSelectedToRender, setMomentSelectedToRender ] = useState<number>();
    
    const handleAddComment = async () => {
        setIsLoadingComments(true);
        if (newComment.trim()) {
            setNewComment(""); // Clear input after submitting
            
            let response = await _updateDisputeComments(newComment);
            await firstCall();
            if (isRight(response)) {
                if (response.right !== "ValidationError") {
                    await firstCall();
                    setIsLoadingComments(false);
                    addToast("The evaluation has been stored", "success", undefined);
                } else {
                    setIsLoadingComments(false);
                    addToast("There was an error creating the evaluation", "error", undefined);
                }
            }
            
        }
    };

    function timeToSeconds(timeStr: string) {
        const [hours, minutes, seconds] = timeStr.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    }

    const _handleMomentSelected = (momentTime: string, moment: number,) => {
        //momentTime
        setMomentSelectedToTrack(momentTime);
        setMomentSelectedToRender(moment);
        let startTime = timeToSeconds(momentTime);
        setAudioCurrentTimeForMoment(startTime);
    }


    
    return (
        <div style={{padding: "20px"}}>
            <ContentLayout 
                header={
                <Header
                    variant="h1"
                >
                    <div>
                        <p style={{margin: "20px"}}>
                            Call Details
                        </p>
                    </div>
                    
                </Header>
                }>
            <div style={{ backgroundColor: "white", padding: "0px 20px 0px 20px", borderRadius: "5px" }}>
                {expandedSection ? (
                    <>
                        {/* Show other two sections in grid above */}
                        <Grid
                            gridDefinition={[
                                { colspan: { l: 6, m: 6, default: 12 } },
                                { colspan: { l: 6, m: 6, default: 12 } }
                            ]}
                        >
                            {expandedSection !== 'callMetadata' && (
                                <Container
                                    fitHeight={true}
                                >
                                    <ExpandableSection
                                        headerText="Call Metadata"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSection(detail.expanded ? 'callMetadata' : null);
                                        }}
                                    >
                                        <SpaceBetween size="m">
                                            {callDetailColumn
                                                .filter(entry => entry.label !== "Entity Recognizer Name")
                                                .map((entry, j) => (
                                                    <ValueWithLabel key={j} label={entry.label}>
                                                        {!data ? (
                                                            <></>
                                                        ) : (
                                                            entry.value(data) || "-"
                                                        )}
                                                    </ValueWithLabel>
                                                ))}
                                        </SpaceBetween>
                                    </ExpandableSection>
                                </Container>
                            )}
                            {expandedSection !== 'transcribeDetails' && (
                                <Container
                                    fitHeight={true}
                                >
                                    <ExpandableSection
                                        headerText="Transcribe Details"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSection(detail.expanded ? 'transcribeDetails' : null);
                                        }}
                                    >
                                        <SpaceBetween size="m">
                                            {transcribeDetailColumn.map((entry, i) => (
                                            <ValueWithLabel key={i} label={entry.label}>
                                                {!data ? (
                                                    <></>
                                                ) : (
                                                entry.value(data) || "-"
                                                )}
                                            </ValueWithLabel>
                                            ))}
                                        </SpaceBetween>
                                    </ExpandableSection>
                                </Container>
                            )}
                            {expandedSection !== 'sentiment' && (
                                <Container>
                                    <ExpandableSection
                                        headerText="Sentiment"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSection(detail.expanded ? 'sentiment' : null);
                                        }}
                                    >
                                        <SentimentChart
                                            data={data?.ConversationAnalytics?.SentimentTrends}
                                            speakerOrder={speakerLabels}
                                            />
                                            <Header variant="h2">Speaker Time</Header>
                                            <SpeakerTimeChart
                                            data={Object.entries(
                                                data?.ConversationAnalytics?.SpeakerTime || {}
                                            ).map(([key, value]) => {
                                                const speakerTime = value as SpeakerTime; // Type assertion
                                                return {
                                                    value: speakerTime.TotalTimeSecs,
                                                    label: speakerLabels[key],
                                                    channel: key
                                                };
                                            })}
                                        />
                                    </ExpandableSection>
                                </Container>
                            )}
                        </Grid>
                        <br />
                        {/* Show expanded section in full width below */}
                        <div style={{ border: '2px solid #0073bb', borderRadius: '16px' }}>
                            <Container
                                fitHeight={expandedSection !== 'sentiment'}
                            >
                            {expandedSection === 'callMetadata' && (
                                <ExpandableSection
                                    headerText="Call Metadata"
                                    expanded={true}
                                    onChange={({ detail }) => {
                                        setExpandedSection(detail.expanded ? 'callMetadata' : null);
                                    }}
                                >
                                    <SpaceBetween size="m">
                                        {callDetailColumn
                                            .filter(entry => entry.label !== "Entity Recognizer Name")
                                            .map((entry, j) => (
                                                <ValueWithLabel key={j} label={entry.label}>
                                                    {!data ? (
                                                        <></>
                                                    ) : (
                                                        entry.value(data) || "-"
                                                    )}
                                                </ValueWithLabel>
                                            ))}
                                    </SpaceBetween>
                                </ExpandableSection>
                            )}
                            {expandedSection === 'transcribeDetails' && (
                                <ExpandableSection
                                    headerText="Transcribe Details"
                                    expanded={true}
                                    onChange={({ detail }) => {
                                        setExpandedSection(detail.expanded ? 'transcribeDetails' : null);
                                    }}
                                >
                                    <SpaceBetween size="m">
                                        {transcribeDetailColumn.map((entry, i) => (
                                        <ValueWithLabel key={i} label={entry.label}>
                                            {!data ? (
                                                <></>
                                            ) : (
                                            entry.value(data) || "-"
                                            )}
                                        </ValueWithLabel>
                                        ))}
                                    </SpaceBetween>
                                </ExpandableSection>
                            )}
                            {expandedSection === 'sentiment' && (
                                <ExpandableSection
                                    headerText="Sentiment"
                                    expanded={true}
                                    onChange={({ detail }) => {
                                        setExpandedSection(detail.expanded ? 'sentiment' : null);
                                    }}
                                >
                                    <SentimentChart
                                        data={data?.ConversationAnalytics?.SentimentTrends}
                                        speakerOrder={speakerLabels}
                                        />
                                        <Header variant="h2">Speaker Time</Header>
                                        <SpeakerTimeChart
                                        data={Object.entries(
                                            data?.ConversationAnalytics?.SpeakerTime || {}
                                        ).map(([key, value]) => {
                                            const speakerTime = value as SpeakerTime; // Type assertion
                                            return {
                                                value: speakerTime.TotalTimeSecs,
                                                label: speakerLabels[key],
                                                channel: key
                                            };
                                        })}
                                    />
                                </ExpandableSection>
                            )}
                            </Container>
                        </div>
                    </>
                ) : (
                    // Show all three sections in grid when collapsed
                    <Grid
                        gridDefinition={[
                            { colspan: { l: 4, m: 4, default: 12 } },
                            { colspan: { l: 4, m: 4, default: 12 } },
                            { colspan: { l: 4, m: 4, default: 12 } }
                        ]}
                    >
                        <Container
                            fitHeight={true}
                        >
                            <ExpandableSection
                                headerText="Call Metadata"
                                expanded={false}
                                onChange={({ detail }) => {
                                    setExpandedSection(detail.expanded ? 'callMetadata' : null);
                                }}
                            >
                                <SpaceBetween size="m">
                                    {callDetailColumn
                                        .filter(entry => entry.label !== "Entity Recognizer Name")
                                        .map((entry, j) => (
                                            <ValueWithLabel key={j} label={entry.label}>
                                                {!data ? (
                                                    <></>
                                                ) : (
                                                    entry.value(data) || "-"
                                                )}
                                            </ValueWithLabel>
                                        ))}
                                </SpaceBetween>
                            </ExpandableSection>
                        </Container>
                        <Container
                            fitHeight={true}
                        >
                            <ExpandableSection
                                headerText="Transcribe Details"
                                expanded={false}
                                onChange={({ detail }) => {
                                    setExpandedSection(detail.expanded ? 'transcribeDetails' : null);
                                }}
                            >
                                <SpaceBetween size="m">
                                    {transcribeDetailColumn.map((entry, i) => (
                                    <ValueWithLabel key={i} label={entry.label}>
                                        {!data ? (
                                            <></>
                                        ) : (
                                        entry.value(data) || "-"
                                        )}
                                    </ValueWithLabel>
                                    ))}
                                </SpaceBetween>
                            </ExpandableSection>
                        </Container>
                        <Container>
                            <ExpandableSection
                                headerText="Sentiment"
                                expanded={false}
                                onChange={({ detail }) => {
                                    setExpandedSection(detail.expanded ? 'sentiment' : null);
                                }}
                            >
                                <SentimentChart
                                    data={data?.ConversationAnalytics?.SentimentTrends}
                                    speakerOrder={speakerLabels}
                                    />
                                    <Header variant="h2">Speaker Time</Header>
                                    <SpeakerTimeChart
                                    data={Object.entries(
                                        data?.ConversationAnalytics?.SpeakerTime || {}
                                    ).map(([key, value]) => {
                                        const speakerTime = value as SpeakerTime; // Type assertion
                                        return {
                                            value: speakerTime.TotalTimeSecs,
                                            label: speakerLabels[key],
                                            channel: key
                                        };
                                    })}
                                />
                            </ExpandableSection>
                        </Container>
                    </Grid>
                )}
                <br />
                {expandedSectionLower ? (
                    <>
                        {/* Show expanded section in full width above */}
                        <div style={{ border: '2px solid #0073bb', borderRadius: '16px' }}>
                            <Container
                                fitHeight={expandedSectionLower !== 'loudnessSentiment' && expandedSectionLower !== 'callAnalyticsSummary'}
                            >
                            {expandedSectionLower === 'loudnessSentiment' && (
                                <ExpandableSection
                                    headerText="Loudness/Sentiment"
                                    expanded={true}
                                    onChange={({ detail }) => {
                                        setExpandedSectionLower(detail.expanded ? 'loudnessSentiment' : null);
                                    }}
                                >
                                    {!loudnessData ? (
                                        <div key='noSpeakers'>No Speakers</div>
                                    ) : (
                                        <LoudnessChart loudnessData={loudnessData} speakerLabels={speakerLabels} />
                                    )}
                                </ExpandableSection>
                            )}
                            {expandedSectionLower === 'entities' && (
                                <ExpandableSection
                                    headerText="Entities"
                                    expanded={true}
                                    onChange={({ detail }) => {
                                        setExpandedSectionLower(detail.expanded ? 'entities' : null);
                                    }}
                                >
                                    {!data ? (
                                        <></>
                                    ) : (
                                        <Entities data={data?.ConversationAnalytics?.CustomEntities} />
                                    )}
                                </ExpandableSection>
                            )}
                            {expandedSectionLower === 'categories' && (
                                <ExpandableSection
                                    headerText="Categories"
                                    expanded={true}
                                    onChange={({ detail }) => {
                                        setExpandedSectionLower(detail.expanded ? 'categories' : null);
                                    }}
                                >
                                    {!data ? (
                                        <></>
                                    ) : (
                                        <ListItems
                                            data={data?.ConversationAnalytics?.CategoriesDetected.map(
                                                (category: any) => category.Name
                                            )}
                                        />
                                    )}
                                </ExpandableSection>
                            )}
                            {expandedSectionLower === 'callAnalyticsSummary' && (
                                <ExpandableSection
                                    headerText="Call Analytics Summary"
                                    expanded={true}
                                    onChange={({ detail }) => {
                                        setExpandedSectionLower(detail.expanded ? 'callAnalyticsSummary' : null);
                                    }}
                                >
                                    {!data ? (
                                        <h4>No summary available.</h4>
                                    ) : (
                                        <SpaceBetween size="l">
                                            <ValueWithLabel key='issues' label="Issue">
                                                {issuesTab()}
                                            </ValueWithLabel>
                                            <ValueWithLabel key='actionItems' label="Action Items">
                                                {actionItemsTab()}
                                            </ValueWithLabel>
                                            <ValueWithLabel key='outcomes' label="Outcomes">
                                                {outcomesTab()}
                                            </ValueWithLabel>
                                        </SpaceBetween>
                                    )}
                                </ExpandableSection>
                            )}
                            {expandedSectionLower === 'generativeAISummary' && (
                                <ExpandableSection
                                    headerText="Generative AI Insights (summary)"
                                    expanded={true}
                                    onChange={({ detail }) => {
                                        setExpandedSectionLower(detail.expanded ? 'generativeAISummary' : null);
                                    }}
                                >
                                    <SpaceBetween size="m">
                                        {summaryFromData.length > 0 ? (
                                            <ValueWithLabel>{summaryFromData}</ValueWithLabel>
                                        ) : (
                                            <ValueWithLabel key="nosummary">No Summary Available</ValueWithLabel>
                                        )}
                                    </SpaceBetween>
                                </ExpandableSection>
                            )}
                            </Container>
                        </div>
                        <br />
                        {/* Show other sections in grid below */}
                        <Grid
                            gridDefinition={[
                                { colspan: { l: 6, m: 6, default: 12 } },
                                { colspan: { l: 6, m: 6, default: 12 } },
                                { colspan: { l: 6, m: 6, default: 12 } },
                                { colspan: { l: 6, m: 6, default: 12 } }
                            ]}
                        >
                            {isTranscribeCallAnalyticsMode && expandedSectionLower !== 'loudnessSentiment' && (
                                <Container>
                                    <ExpandableSection
                                        headerText="Loudness/Sentiment"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSectionLower(detail.expanded ? 'loudnessSentiment' : null);
                                        }}
                                    >
                                        {!loudnessData ? (
                                            <div key='noSpeakers'>No Speakers</div>
                                        ) : (
                                            <LoudnessChart loudnessData={loudnessData} speakerLabels={speakerLabels} />
                                        )}
                                    </ExpandableSection>
                                </Container>
                            )}
                            {expandedSectionLower !== 'entities' && (
                                <Container 
                                    fitHeight={true}
                                >
                                    <ExpandableSection
                                        headerText="Entities"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSectionLower(detail.expanded ? 'entities' : null);
                                        }}
                                    >
                                        {!data ? (
                                            <></>
                                        ) : (
                                            <Entities data={data?.ConversationAnalytics?.CustomEntities} />
                                        )}
                                    </ExpandableSection>
                                </Container>
                            )}
                            {isTranscribeCallAnalyticsMode && expandedSectionLower !== 'categories' && (
                                <Container
                                    fitHeight={true}
                                >
                                    <ExpandableSection
                                        headerText="Categories"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSectionLower(detail.expanded ? 'categories' : null);
                                        }}
                                    >
                                        {!data ? (
                                            <></>
                                        ) : (
                                            <ListItems
                                                data={data?.ConversationAnalytics?.CategoriesDetected.map(
                                                    (category: any) => category.Name
                                                )}
                                            />
                                        )}
                                    </ExpandableSection>
                                </Container>
                            )}
                            {isTranscribeCallAnalyticsMode && expandedSectionLower !== 'callAnalyticsSummary' && (
                                <Container
                                    fitHeight={true}
                                >
                                    <ExpandableSection
                                        headerText="Call Analytics Summary"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSectionLower(detail.expanded ? 'callAnalyticsSummary' : null);
                                        }}
                                    >
                                        {!data ? (
                                            <h4>No summary available.</h4>
                                        ) : (
                                            <SpaceBetween size="l">
                                                <ValueWithLabel key='issues' label="Issue">
                                                    {issuesTab()}
                                                </ValueWithLabel>
                                                <ValueWithLabel key='actionItems' label="Action Items">
                                                    {actionItemsTab()}
                                                </ValueWithLabel>
                                                <ValueWithLabel key='outcomes' label="Outcomes">
                                                    {outcomesTab()}
                                                </ValueWithLabel>
                                            </SpaceBetween>
                                        )}
                                    </ExpandableSection>
                                </Container>
                            )}
                            {expandedSectionLower !== 'generativeAISummary' && (
                                <Container
                                    fitHeight={true}
                                >
                                    <ExpandableSection
                                        headerText="Generative AI Insights (summary)"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSectionLower(detail.expanded ? 'generativeAISummary' : null);
                                        }}
                                    >
                                        <SpaceBetween size="m">
                                            {summaryFromData.length > 0 ? (
                                                <ValueWithLabel>{summaryFromData}</ValueWithLabel>
                                            ) : (
                                                <ValueWithLabel key="nosummary">No Summary Available</ValueWithLabel>
                                            )}
                                        </SpaceBetween>
                                    </ExpandableSection>
                                </Container>
                            )}
                        </Grid>
                    </>
                ) : (
                    // Show all sections in grid when collapsed
                    <>
                        {isTranscribeCallAnalyticsMode && (
                            <>
                                <Container>
                                    <ExpandableSection
                                        headerText="Loudness/Sentiment"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSectionLower(detail.expanded ? 'loudnessSentiment' : null);
                                        }}
                                    >
                                        {!loudnessData ? (
                                            <div key='noSpeakers'>No Speakers</div>
                                        ) : (
                                            <LoudnessChart loudnessData={loudnessData} speakerLabels={speakerLabels} />
                                        )}
                                    </ExpandableSection>
                                </Container>
                                <br />
                            </>
                        )}
                        <Grid
                            gridDefinition={[
                                { colspan: { l: 6, m: 6, default: 12 } },
                                { colspan: { l: 6, m: 6, default: 12 } },
                                { colspan: { l: 6, m: 6, default: 12 } },
                                { colspan: { l: 6, m: 6, default: 12 } }
                            ]}
                        >
                            <Container 
                                fitHeight={true}
                            >
                                <ExpandableSection
                                    headerText="Entities"
                                    expanded={false}
                                    onChange={({ detail }) => {
                                        setExpandedSectionLower(detail.expanded ? 'entities' : null);
                                    }}
                                >
                                    {!data ? (
                                        <></>
                                    ) : (
                                        <Entities data={data?.ConversationAnalytics?.CustomEntities} />
                                    )}
                                </ExpandableSection>
                            </Container>
                            {isTranscribeCallAnalyticsMode && (
                                <Container
                                    fitHeight={true}
                                >
                                    <ExpandableSection
                                        headerText="Categories"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSectionLower(detail.expanded ? 'categories' : null);
                                        }}
                                    >
                                        {!data ? (
                                            <></>
                                        ) : (
                                            <ListItems
                                                data={data?.ConversationAnalytics?.CategoriesDetected.map(
                                                    (category: any) => category.Name
                                                )}
                                            />
                                        )}
                                    </ExpandableSection>
                                </Container>
                            )}
                            {isTranscribeCallAnalyticsMode && (
                                <Container
                                    fitHeight={true}
                                >
                                    <ExpandableSection
                                        headerText="Call Analytics Summary"
                                        expanded={false}
                                        onChange={({ detail }) => {
                                            setExpandedSectionLower(detail.expanded ? 'callAnalyticsSummary' : null);
                                        }}
                                    >
                                        {!data ? (
                                            <h4>No summary available.</h4>
                                        ) : (
                                            <SpaceBetween size="l">
                                                <ValueWithLabel key='issues' label="Issue">
                                                    {issuesTab()}
                                                </ValueWithLabel>
                                                <ValueWithLabel key='actionItems' label="Action Items">
                                                    {actionItemsTab()}
                                                </ValueWithLabel>
                                                <ValueWithLabel key='outcomes' label="Outcomes">
                                                    {outcomesTab()}
                                                </ValueWithLabel>
                                            </SpaceBetween>
                                        )}
                                    </ExpandableSection>
                                </Container>
                            )}
                            <Container
                                fitHeight={true}
                            >
                                <ExpandableSection
                                    headerText="Generative AI Insights (summary)"
                                    expanded={false}
                                    onChange={({ detail }) => {
                                        setExpandedSectionLower(detail.expanded ? 'generativeAISummary' : null);
                                    }}
                                >
                                    <SpaceBetween size="m">
                                        {summaryFromData.length > 0 ? (
                                            <ValueWithLabel>{summaryFromData}</ValueWithLabel>
                                        ) : (
                                            <ValueWithLabel key="nosummary">No Summary Available</ValueWithLabel>
                                        )}
                                    </SpaceBetween>
                                </ExpandableSection>
                            </Container>
                        </Grid>
                    </>
                )}
                <br />
                <Container>
                    <ExpandableSection
                        headerText="Generative AI Insights (questions)"
                        expanded={expandedQuestions}
                        onChange={({ detail }) => {
                            setExpandedQuestions(detail.expanded);
                        }}
                    >
                        {(questionsFromData?.length ?? 0) > 0 ? (
                            <div style={{width: 'calc(100vw - 370px)'}}>
                            {
                                questionsFromData?.map((q: any) => {
                                    const questionId = q.questionId || q.id;
                                    const isQuestionExpanded = expandedQuestionTexts.has(questionId);
                                    const questionLength = q.question?.length || 0;
                                    const showSeeMore = questionLength > 150;
                                    const displayQuestion = showSeeMore && !isQuestionExpanded 
                                        ? q.question.substring(0, 50) + '...' 
                                        : q.question;
                                    
                                    return(
                                    <div style={{border: "1px solid #dbdbdb", marginBottom: "20px", padding: "8px", borderRadius: '6px'}}>
                                        <div style={{ backgroundColor: '#f7f5f5', padding: '20px', display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                                            <div style={{ width: '60%'}}>
                                                <div style={{ fontWeight: "bold" }}>
                                                    {displayQuestion}
                                                    {showSeeMore && (
                                                        <span 
                                                            style={{ 
                                                                color: '#0073bb', 
                                                                cursor: 'pointer', 
                                                                marginLeft: '5px',
                                                                textDecoration: 'underline'
                                                            }}
                                                            onClick={() => {
                                                                const newSet = new Set(expandedQuestionTexts);
                                                                if (isQuestionExpanded) {
                                                                    newSet.delete(questionId);
                                                                } else {
                                                                    newSet.add(questionId);
                                                                }
                                                                setExpandedQuestionTexts(newSet);
                                                            }}
                                                        >
                                                            {isQuestionExpanded ? ' See less' : ' See more'}
                                                        </span>
                                                    )}
                                                </div>
                                                {q.response}
                                                <div style={{ marginTop: '5px' }}>
                                                        <div className='applicable_options_container'>
                                                            <label className='applicable_options' style={{color: q.status === true ? '#f50000' : (hasInDispute ? 'gray' : '')}}>
                                                                <input
                                                                    type="checkbox"
                                                                    disabled={hasInDispute}
                                                                    checked={q.status === true}
                                                                    onChange={(e) => _handleStatusChange(e.target.value, q.questionId)} 
                                                                /> 
                                                                In dispute
                                                            </label>
                                                        </div>
                                                </div>
                                            </div>
                                            <div style={{display: 'flex', width: '40%', justifyContent: 'space-between' }}>
                                                <div className='ai_score'>
                                                        <span className='ai_score_label'>Max points: </span>{`${q.weight === null ? 'No data': q.weight}`}
                                                </div>
                                                
                                                {
                                                    q.autofail &&
                                                    <div className='ai_score'>
                                                        <span className='ai_score_label'>Autofail </span>
                                                    </div>
                                                }
                                                {
                                                    q.partial !== false && 
                                                    <div className='ai_score'>
                                                        <span className='ai_score_label'>Partial </span>
                                                    </div>
                                                }
                                            </div>
                                            
                                        </div>
                                        <div style={{width: '100%', minWidth: '100%', display: 'flex', padding: '0px'}}>
                                            <div style={{backgroundColor: '#f7f5f5', marginRight: '10px', width: '20%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                <div key={q.id}>
                                                    <div className='ai_score'>
                                                        <span className='ai_score_label'>AI Score: </span>{`${q.aiScore === null ? '--': ([5,50].includes(q.aiScore) ? 'Partial' : ([1,10,100].includes(q.aiScore) ? 'Yes': ([0].includes(q.aiScore) ? 'No': '--') ))}`}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div style={{ backgroundColor: '#f7f5f5',marginLeft: '0px', width: '80%'}}>
                                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-around', padding: '10px'}}>
                                                <div style={{width: '50%'}}>
                                                    Comment:
                                                    <br />
                                                    <input className='ai_manual_comment' type='text' style={{width: '100%'}}
                                                        value={q.comment}
                                                        onChange={(e) => _handleCommentChange(e.target.value, q.question.replaceAll(" ", "").toLocaleLowerCase())}
                                                    />
                                                </div>
                                                <div style={{ marginTop: '10px' }}>
                                                    <div>
                                                        <div style={{ marginLeft: '10px' }}>
                                                        <label htmlFor="">Manual Score</label>
                                                        <div className='applicable_options_container'>
                                                            {
                                                            q.question.replaceAll(" ", "").toLocaleLowerCase() ===
                                                            question34 ? (
                                                                <>
                                                                <label className='applicable_options'>
                                                                    <input
                                                                    type="radio"
                                                                    value="Email Address"
                                                                    checked={agentVerifyOptions === "Email Address"}
                                                                    onChange={(e) =>
                                                                        setAgentVerifyOptions("Email Address")
                                                                    }
                                                                    />
                                                                    Email Address
                                                                </label>
                                                                <label className='applicable_options'>
                                                                    <input
                                                                    type="radio"
                                                                    value="Contact Number"
                                                                    checked={agentVerifyOptions === "Contact Number"}
                                                                    onChange={(e) =>
                                                                        setAgentVerifyOptions("Contact Number")
                                                                    }
                                                                    />
                                                                    Contact Number
                                                                </label>
                                                                <label className='applicable_options'>
                                                                    <input
                                                                    type="radio"
                                                                    value="Contact Number & Email Address"
                                                                    checked={agentVerifyOptions === "Contact Number & Email Address"}
                                                                    onChange={(e) =>
                                                                        setAgentVerifyOptions("Contact Number & Email Address")
                                                                    }
                                                                    />
                                                                    Contact Number & Email Address
                                                                </label>
                                                                <label className='applicable_options'>
                                                                    <input
                                                                    type="radio"
                                                                    value="N/A"
                                                                    checked={agentVerifyOptions === "N/A"}
                                                                    onChange={(e) =>
                                                                        setAgentVerifyOptions("N/A")
                                                                    }
                                                                    />
                                                                    N/A
                                                                </label>
                                                                </>
                                                            ) : (
                                                                <>
                                                                <label className='applicable_options'>
                                                                    <input
                                                                    type="radio"
                                                                    value="yes"
                                                                    checked={q.score === "yes"}
                                                                    onChange={(e) =>
                                                                        _handleScoreChange(e.target.value, q.question.replaceAll(" ", "").toLocaleLowerCase())
                                                                    }
                                                                    />
                                                                    Yes
                                                                </label>
                                                                {q.partial !== false && (
                                                                    <label className='applicable_options'>
                                                                    <input
                                                                        type="radio"
                                                                        value="partial"
                                                                        checked={q.score === "partial"}
                                                                        disabled={q.partial === false}
                                                                        onChange={(e) =>
                                                                        _handleScoreChange(e.target.value, q.question.replaceAll(" ", "").toLocaleLowerCase())
                                                                        }
                                                                    />
                                                                    Partial
                                                                    </label>
                                                                )}
                                                                <label className='applicable_options'>
                                                                    <input
                                                                    type="radio"
                                                                    value="no"
                                                                    checked={q.score === "no"}
                                                                    onChange={(e) =>
                                                                        _handleScoreChange(e.target.value, q.question.replaceAll(" ", "").toLocaleLowerCase())
                                                                    }
                                                                    />
                                                                    No
                                                                </label>
                                                                <label className='applicable_options'>
                                                                    <input
                                                                    type="radio"
                                                                    value="notApplicable"
                                                                    checked={q.score === "notApplicable"}
                                                                    onChange={(e) =>
                                                                        _handleScoreChange(e.target.value, q.question.replaceAll(" ", "").toLocaleLowerCase())
                                                                    }
                                                                    />
                                                                    N/A
                                                                </label>
                                                                    {q.score !== "notApplicable" && q.realScore !== "autofail" && (
                                                                        <div className='applicable_options'>
                                                                            {`(Score: ${q.weight * (q.score === "partial" ? 5 : q.score === "yes" ? 10 : 0) / 10})`}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )
                                                            }
                                                        </div>
                                                        </div>
                                                    </div>
                                                    </div>

                                            </div>
                                            </div>
                                        </div>
                                        
                                        
                                    </div>
                                )})
                            }
                            {
                                hasInDispute !== true &&
                                <>
                                {
                                    !isLoadingDispute ?
                                    <ButtonComponent text={"Start Dispute"} type={ButtonType.MAIN} onClick={_handleStartDispute} icon={<Icons.TaskList />} /> 
                                    :
                                    <LoadingComponent />
                                }
                                </>
                                    
                            }
                            {
                                hasInDispute === true &&
                                <>
                                {
                                    canEditDisputeSatus &&
                                    <>
                                    <div className='ai_score' style={{width: '100%', marginBottom: '20px', marginTop: '30px', border: "1px solid #dbdbdb", padding: '20px 10px 20px 10px'}}>
                                        <span className='ai_score_label' style={{fontSize: '16px'}}>Dispute Status: </span>
                                        <Select
                                            className="flex-grow-1 anget_filter_up"
                                                options={(status).map((code, i) => ({
                                                    label: code,
                                                    value: code,
                                                }))}
                                            onChange={(event: any) =>{
                                                setCallStatus(event.label)
                                            }
                                                
                                            }
                                            value={
                                                callStatus
                                                    ? { label: callStatus, value: callStatus }
                                                    : null
                                                }
                                        />
                                    </div>
                                    {
                                        !isLoadingDispute ?
                                        <ButtonComponent text={"Update Dispute Status"} type={ButtonType.MAIN} onClick={() => _updateStatusCall()} icon={<Icons.Plus />} /> 
                                        :
                                        <LoadingComponent />
                                    }
                                    </>
                                    
                                    
                                }
                                    <div style={{marginTop: '30px', marginBottom: '20px'}}>
                                        <span className='ai_score_label' style={{fontSize: '16px'}}>Dispute Comments: </span>
                                    </div>
                                    <div className="max-w-md mx-auto p-4 border rounded-lg shadow-md" style={{marginBottom: '20px'}}>
                                        <div className="space-y-2">
                                            {comments?.map((comment: any, index: number) => (
                                            <p key={index} className="text-sm">
                                                <strong>{comment.evaluator}:</strong> {comment.comment}
                                            </p>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <span className='ai_score_label' style={{fontSize: '16px'}}>Comment: </span>
                                    <div className='ai_score' style={{width: '100%', marginBottom: '20px', marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: "1px solid #dbdbdb", padding: '20px 10px 20px 10px'}}>
                                            
                                                <div style={{width: '60%'}}>
                                                    <input className='ai_manual_comment' type='text' style={{width: '100%'}}
                                                        value={newComment}
                                                        onChange={(e) => setNewComment(e.target.value)}
                                                    />
                                            </div>
                                            <div>
                                                {
                                                    !isLoadingCommnets ?
                                                    <ButtonComponent disabled={!newComment.trim()} text={"Send Dispute Comment"} type={ButtonType.MAIN} onClick={() => handleAddComment()} icon={<Icons.Plus height={30} />} /> 
                                                    :
                                                    <LoadingComponent />
                                                }
                                                
                                            </div>
                                            
                                    </div>
                                </>
                            }

                            </div>
                        ) : (
                            <ValueWithLabel key="noquestions">No Questions Available</ValueWithLabel>
                        )}

                        <div>
                            <p className='questions_scores_title'>Total Score Evaluation</p>
                            <div className='questions_scores_container'>
                                <div className='score_item'>
                                    <p>AI score</p>
                                    <div>
                                        {isNaN(sumScores.aiScore) ? sumScores.aiScore : `${(Math.floor(sumScores.aiScore  * 100) / 100).toFixed(2)}%`}
                                    </div>
                                </div>
                                
                                <div className='score_item'>
                                    <p>Manual score</p>
                                    <div>
                                        {isNaN(sumScores.manualScore) ? sumScores.manualScore : `${(Math.floor(sumScores.manualScore  * 100) / 100).toFixed(2)}%`}
                                    </div>
                                </div>
                                {
                                    !isNaN(sumScores.manualScore) &&
                                <div className='score_item'>
                                    <p>Total score/ Total weight</p>
                                    <div>
                                    {`${sumTotalEvaluationPerQuestion['sumScore']}/${sumTotalEvaluationPerQuestion['sumWeight']}`}
                                    </div>
                                </div>
                                }
                                
                                <div>
                                    
                                </div>
                            </div>
                            
                        </div>
                        
                        {
                            user?.permissions?.aiEvaluation.update &&
                            <div className="button-container">
                            {
                                !isLoading ?
                                <>
                                    <ButtonComponent text={"Update Call"} type={ButtonType.MAIN} onClick={_handleUpdateComments} icon={<Icons.EditPencil height={30} />} />
                                    <ButtonComponent text={"Request New AI Evaluation"} type={ButtonType.MAIN} onClick={_handleRequestNewAIEvaluation} icon={<Icons.Plus height={30}/>} />
                                    <ButtonComponent text={"Upload Call File"} type={ButtonType.MAIN} onClick={() => {
                                        openModalCustom('md', 'Upload Call File', <UploadChannelModal defaultChannel="calls" onSuccess={firstCall} />);
                                    }} icon={<Icons.Plus height={30}/>} />
                                </>
                                
                                :
                                <LoadingComponent />
                            }
                            </div>
                                
                        }
                    </ExpandableSection>
                </Container>
                    <br />
                    <Container>
                        <ExpandableSection
                            headerText="Moments"
                            expanded={expandedMoments}
                            onChange={({ detail }) => {
                                setExpandedMoments(detail.expanded);
                            }}
                        >
                            <Container className='moment_positive_container'>
                                <ExpandableSection
                                    headerText="Positive Moments"
                                    expanded={expandedMomentSubsection === 'positive'}
                                    onChange={({ detail }) => {
                                        setExpandedMomentSubsection(detail.expanded ? 'positive' : null);
                                    }}
                                >
                        {
                            moments.positive?.length > 0 ?
                                <div>
                                    {
                                        moments.positive.map((q: any) => {
                                            
                                            return (
                                                <div  style={{border: "1px solid #dbdbdb", backgroundColor: '#f7f5f5', marginBottom: "20px", padding: "8px", borderRadius: '6px'}}>
                                                    <div style={{padding: '20px', display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                                                        <div style={{ width: '60%'}}>
                                                            <div style={{ fontWeight: "bold" }}>
                                                                {q[0]}
                                                            </div>
                                                            <div style={{marginTop: '10px', width: '100%', display: 'flex', alignItems: 'center'}}>
                                                                <div key={q.id}>
                                                                    <div className='ai_score'>
                                                                        <span className='ai_score_label'>AI Score: </span>{`${aiMoments[`${q[3]}`][1] === null ? '--':  (aiMoments[`${q[3]}`][1] === 1 ? 'Yes': 'No')}`}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div style={{marginLeft: '20px'}}>
                                                            {
                                                                /**
                                                                 * <div className='ai_score'>
                                                                    <span className='ai_score_label'>Theme: </span>{`${q.weight === null ? 'No data': SCORES[`${q[2]}`]}`}
                                                                    </div>
                                                                 * 
                                                                 */
                                                            }
                                                            
                                                            <div>
                                                            <label htmlFor="">Manual Score</label>
                                                                <div className='applicable_options_container'>
                                                                    <label className='applicable_options'>
                                                                        <input 
                                                                            type="radio" 
                                                                            value="yes" 
                                                                            checked={q[1] === 1}
                                                                            onChange={(e) => _handleMomentEvaluation(e.target.value, q[0].replaceAll(" ", "").toLocaleLowerCase(), q[3])} 
                                                                        /> 
                                                                        Yes
                                                                    </label>
                                                                    <label className='applicable_options'>
                                                                        <input 
                                                                            type="radio" 
                                                                            value="no" 
                                                                            checked={q[1] === 0}
                                                                            onChange={(e) => _handleMomentEvaluation(e.target.value, q[0].replaceAll(" ", "").toLocaleLowerCase(), q[3])} 
                                                                        /> 
                                                                        No
                                                                    </label>
                                                                
                                                                </div>
                                                                {q[1] === 1 &&
                                                                    <>
                                                                        {mockApiResponse && 
                                                                            <div className="">
                                                                                {mockApiResponse && Object.entries(mockApiResponse).map(([sectionKey, sectionOptions]) => (
                                                                                    <div key={sectionKey} style={{marginBottom: '10px'}}>
                                                                                        <p>{optionsToReplace[sectionKey.replace(/([A-Z])/g, ' $1')]}</p>
                                                                                        <div style={{border: '1px solid black', padding: '10px'}}>
                                                                                            {(sectionOptions as any).map((option: any) => (
                                                                                                <label key={option} className="" style={{ marginRight: "10px"}}>
                                                                                                <input
                                                                                                    style={{ marginRight: "2px"}}
                                                                                                    type="radio"
                                                                                                    value={sectionKey === 'options' ? q[6] : q[7]}
                                                                                                    checked={(sectionKey === 'options' ? q[6] : q[7]) === option}
                                                                                                    onChange={() => handleChange(q[3], sectionKey, option)}
                                                                                                />
                                                                                                <span className="">{option}</span>
                                                                                                </label>
                                                                                            ))}
                                                                                        </div>
                                                                                        
                                                                                    </div>
                                                                                ))}

                                                                            </div>
                                                                            }
                                                                    </>
                                                                }
                                                                
                                                                {/**
                                                                 * 
                                                                 */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                            </div>  
                                            )
                                        })
                                    }
                                </div>
                                : (
                                    <ValueWithLabel key="noquestions">No Positive Moments Available</ValueWithLabel>
                                )
                        }
                                </ExpandableSection>
                            </Container>
                            <br />
                            <br />
                            
                            <Container className='moment_neutral_container'>
                                <ExpandableSection
                                    headerText="Neutral Moments"
                                    expanded={expandedMomentSubsection === 'neutral'}
                                    onChange={({ detail }) => {
                                        setExpandedMomentSubsection(detail.expanded ? 'neutral' : null);
                                    }}
                                >
                        {
                            moments.neutral?.length > 0 ?
                            <div>
                                {
                                moments.neutral?.map((q: any) => {
                                    
                                    return (
                                        <div style={{border: "1px solid #dbdbdb", backgroundColor: '#f7f5f5', marginBottom: "20px", padding: "8px", borderRadius: '6px'}}>
                                            <div style={{padding: '20px', display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                                                <div style={{ width: '60%'}}>
                                                    <div style={{ fontWeight: "bold" }}>
                                                        {q[0]}
                                                    </div>
                                                    <div style={{marginTop: '10px', width: '100%', display: 'flex', alignItems: 'center'}}>
                                                        <div key={q.id}>
                                                            <div className='ai_score'>
                                                                <span className='ai_score_label'>AI Score: </span>{`${aiMoments[`${q[3]}`][1] === null ? '--':  (aiMoments[`${q[3]}`][1] === 1 ? 'Yes': 'No')}`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{marginLeft: '20px' }}>
                                                    {
                                                        /**
                                                         * <div className='ai_score'>
                                                            <span className='ai_score_label'>Theme: </span>{`${q.weight === null ? 'No data': q[2]}`}
                                                    </div>
                                                         */
                                                    }
                                                    <div>
                                                        <div className='applicable_options_container'>
                                                            <label className='applicable_options'>
                                                                <input 
                                                                    type="radio" 
                                                                    value="yes" 
                                                                    checked={q[1] === 1}
                                                                    onChange={(e) => _handleMomentEvaluation(e.target.value, q[0].replaceAll(" ", "").toLocaleLowerCase(), q[3])} 
                                                                /> 
                                                                Yes
                                                            </label>
                                                            <label className='applicable_options'>
                                                                <input 
                                                                    type="radio" 
                                                                    value="no" 
                                                                    checked={q[1] === 0}
                                                                    onChange={(e) => _handleMomentEvaluation(e.target.value, q[0].replaceAll(" ", "").toLocaleLowerCase(), q[3])} 
                                                                /> 
                                                                No
                                                            </label>
                                                        
                                                        </div>
                                                            {q[1] === 1 &&
                                                                    <>
                                                                        {mockApiResponse && 
                                                                            <div className="">
                                                                                {mockApiResponse && Object.entries(mockApiResponse).map(([sectionKey, sectionOptions]) => (
                                                                                    <div key={sectionKey} style={{marginBottom: '10px'}}>
                                                                                        <p>{optionsToReplace[sectionKey.replace(/([A-Z])/g, ' $1')]}</p>
                                                                                        <div style={{border: '1px solid black', padding: '10px'}}>
                                                                                            {(sectionOptions as any).map((option: any) => (
                                                                                                <label key={option} className="" style={{ marginRight: "10px"}}>
                                                                                                <input
                                                                                                    style={{ marginRight: "2px"}}
                                                                                                    type="radio"
                                                                                                    value={sectionKey === 'options' ? q[6] : q[7]}
                                                                                                    checked={(sectionKey === 'options' ? q[6] : q[7]) === option}
                                                                                                    onChange={() => handleChange(q[3], sectionKey, option)}
                                                                                                />
                                                                                                <span className="">{option}</span>
                                                                                                </label>
                                                                                            ))}
                                                                                        </div>
                                                                                        
                                                                                    </div>
                                                                                ))}

                                                                            </div>
                                                                            }
                                                                    </>
                                                                }
                                                        {/**
                                                         *
                                                         */}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                    </div>  
                                    )
                                })
                            }
                            </div>
                            : (
                                <ValueWithLabel key="noquestions">No Neutral Moments Available</ValueWithLabel>
                            )
                        }
                                </ExpandableSection>
                            </Container>
                            <br />
                            <Container className='moment_negative_container' >
                                <ExpandableSection
                                    headerText="Negative Moments"
                                    expanded={expandedMomentSubsection === 'negative'}
                                    onChange={({ detail }) => {
                                        setExpandedMomentSubsection(detail.expanded ? 'negative' : null);
                                    }}
                                >
                        {
                            moments.negative?.length > 0 ?
                            <div>
                                {
                                    moments.negative?.map((q: any) => {
                                        return (
                                            <div style={{border: "1px solid #dbdbdb", backgroundColor: '#f7f5f5', marginBottom: "20px", padding: "8px", borderRadius: '6px'}}>
                                                <div style={{padding: '20px', display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                                                    <div style={{ width: '60%'}}>
                                                        <div style={{ fontWeight: "bold" }}>
                                                            {q[0]}
                                                        </div>
                                                        <div style={{marginTop: '10px', width: '100%', display: 'flex', alignItems: 'center'}}>
                                                            <div key={q.id}>
                                                                <div className='ai_score'>
                                                                    <span className='ai_score_label'>AI Score: </span>{`${aiMoments[`${q[3]}`][1] === null ? '--': (aiMoments[`${q[3]}`][1] === 1 ? 'Yes': 'No')}`}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{marginLeft: '20px'}}>
                                                        {/**
                                                         * <div className='ai_score'>
                                                                <span className='ai_score_label'>Theme: </span>{`${q.weight === null ? 'No data': q[2]}`}
                                                        </div>
                                                        */}
                                                        <div>
                                                            <label htmlFor="">Manual Score</label>
                                                            <div className='applicable_options_container'>
                                                                <label className='applicable_options'>
                                                                    <input 
                                                                        type="radio" 
                                                                        value="yes" 
                                                                        checked={q[1] === 1}
                                                                        onChange={(e) => _handleMomentEvaluation(e.target.value, q[0].replaceAll(" ", "").toLocaleLowerCase(), q[3])} 
                                                                    /> 
                                                                    Yes
                                                                </label>
                                                                <label className='applicable_options'>
                                                                    <input 
                                                                        type="radio" 
                                                                        value="no" 
                                                                        checked={q[1] === 0}
                                                                        onChange={(e) => _handleMomentEvaluation(e.target.value, q[0].replaceAll(" ", "").toLocaleLowerCase(), q[3])} 
                                                                    /> 
                                                                    No
                                                                </label>
                                                            
                                                            </div>
                                                             {q[1] === 1 &&
                                                                    <>
                                                                        {mockApiResponse && 
                                                                            <div className="">
                                                                                {mockApiResponse && Object.entries(mockApiResponse).map(([sectionKey, sectionOptions]) => (
                                                                                    <div key={sectionKey} style={{marginBottom: '10px'}}>
                                                                                        <p>{optionsToReplace[sectionKey.replace(/([A-Z])/g, ' $1')]}</p>
                                                                                        <div style={{border: '1px solid black', padding: '10px'}}>
                                                                                            {(sectionOptions as any).map((option: any) => (
                                                                                                <label key={option} className="" style={{ marginRight: "10px"}}>
                                                                                                <input
                                                                                                    style={{ marginRight: "2px"}}
                                                                                                    type="radio"
                                                                                                    value={sectionKey === 'options' ? q[6] : q[7]}
                                                                                                    checked={(sectionKey === 'options' ? q[6] : q[7]) === option}
                                                                                                    onChange={() => handleChange(q[3], sectionKey, option)}
                                                                                                />
                                                                                                <span className="">{option}</span>
                                                                                                </label>
                                                                                            ))}
                                                                                        </div>
                                                                                        
                                                                                    </div>
                                                                                ))}

                                                                            </div>
                                                                            }
                                                                    </>
                                                                }
                                                            {/**
                                                             * 
                                                             * <label htmlFor="">Manual Score</label>
                                                            <div className='applicable_options_container'>
                                                                <label className='applicable_options'>
                                                                    <input 
                                                                        type="radio" 
                                                                        value="yes" 
                                                                        checked={q[1] === 1}
                                                                        onChange={(e) => _handleMomentEvaluation(e.target.value, q[0].replaceAll(" ", "").toLocaleLowerCase(), q[3])} 
                                                                    /> 
                                                                    Yes
                                                                </label>
                                                                <label className='applicable_options'>
                                                                    <input 
                                                                        type="radio" 
                                                                        value="no" 
                                                                        checked={q[1] === 0}
                                                                        onChange={(e) => _handleMomentEvaluation(e.target.value, q[0].replaceAll(" ", "").toLocaleLowerCase(), q[3])} 
                                                                    /> 
                                                                    No
                                                                </label>
                                                            
                                                            </div>
                                                             */}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                        </div>  
                                        )
                                    })
                                }
                            </div>
                            : (
                                <ValueWithLabel key="noquestions">No Negative Moments Available</ValueWithLabel>
                            )
                        }
                                </ExpandableSection>
                            </Container>
                            <br />

                            {
                                !isLoadingMoment ?
                                    <ButtonComponent text={"Update Moments"} type={ButtonType.MAIN} onClick={_handleUpdateMoments} icon={<Icons.TaskList />}></ButtonComponent>
                                    :
                                    <LoadingComponent />
                            }
                        </ExpandableSection>
                    </Container>
                    <br />
                    <Container>
                        <ExpandableSection
                            headerText="Audio & Transcription"
                            expanded={expandedAudioTranscription}
                            onChange={({ detail }) => {
                                setExpandedAudioTranscription(detail.expanded);
                            }}
                        >
                            <div>
                                {audioData !== "" && data && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <audio
                                            key='audoiElem'
                                            className="float-end"
                                            ref={audioElem}
                                            controls
                                            src={
                                                data["ConversationAnalytics"]["SourceInformation"][0]
                                                ["TranscribeJobInfo"]["MediaFileUri"]
                                            }
                                            onTimeUpdate={onAudioPlayTimeUpdate}
                                        >
                                            Your browser does not support the
                                            <code>audio</code> element.
                                        </audio>
                                    </div>
                                )}
                            </div>
                            <div>
                            {!data? (
                                <></>
                            ) : 
                            <>
                            <div className='download_transcription_container'>
                                
                                <div className='download_transcription_container_inner'>
                                    <Icons.Download height={20} />
                                    <div style={{width: '8px'}}></div>
                                    <CSVLink data={csvData} className="download_transcription_button" filename={`${recordURL.split('.')[0]}.csv`}>Download Transcription</CSVLink>
                                </div>
                                {
                                !isLoadingSwap ?
                                    <ButtonComponent text={"Swap Agent/Caller"} type={ButtonType.MAIN} onClick={() => _updateSwapCall()} icon={<Icons.ViewEye color='white' height={26} />}></ButtonComponent>
                                    :
                                    <LoadingComponent />
                                }
                                
                            </div>
                            <div>
                                <h5> Moment to track</h5>
                                <p>{aiMoments[`${momentSelectedToRender}`]?.[0]}</p>
                                <div className='moment_to_track_container'>
                                {
                                    Object.entries(aiMoments).map(([key, value]) => {
                                        let options = value as any;
                                        
                                        return (
                                            <>
                                            {options[8] &&
                                            <div 
                                                key={key}
                                                className={momentSelectedToRender === options[3] ? 'moment_to_track_inner_selected':'moment_to_track_inner'}
                                                onClick={() => _handleMomentSelected(options[8] || "", options[3])}
                                            >
                                                {`Moment ${options[3]}`}
                                            </div>
                                            }
                                            </>
                                        );
                                    })
                                }
                                </div>
                            </div>
                            <div ref={transcriptElem}>
                            {
                                (() => {
                                    // Group segments by 60-second intervals
                                    const segments = data["SpeechSegments"] || [];
                                    const groupedSegments: { [key: number]: any[] } = {};
                                    
                                    segments.forEach((s: any) => {
                                        // Calculate which 60-second interval this segment belongs to
                                        const intervalStart = Math.floor(s.SegmentStartTime / 60) * 60;
                                        if (!groupedSegments[intervalStart]) {
                                            groupedSegments[intervalStart] = [];
                                        }
                                        groupedSegments[intervalStart].push(s);
                                    });
                                    
                                    // Sort intervals by start time
                                    const sortedIntervals = Object.keys(groupedSegments)
                                        .map(Number)
                                        .sort((a, b) => a - b);
                                    
                                    // Format time for display
                                    const formatTime = (seconds: number) => {
                                        const mins = Math.floor(seconds / 60);
                                        const secs = Math.floor(seconds % 60);
                                        return `${mins}:${secs.toString().padStart(2, '0')}`;
                                    };
                                    
                                    return (
                                        <div>
                                            {sortedIntervals.map((intervalStart) => {
                                                const intervalEnd = intervalStart + 60;
                                                const intervalSegments = groupedSegments[intervalStart];
                                                const sectionKey = intervalStart;
                                        
                                                return (
                                                    <div key={sectionKey} style={{ marginBottom: '10px', width: '100%' }}>
                                                        <Container>
                                                            <ExpandableSection
                                                                headerText={`${formatTime(intervalStart)} - ${formatTime(intervalEnd)}`}
                                                                expanded={expandedTranscriptSections.has(sectionKey)}
                                                                onChange={({ detail }) => {
                                                                    const newSet = new Set(expandedTranscriptSections);
                                                                    if (detail.expanded) {
                                                                        newSet.add(sectionKey);
                                                                    } else {
                                                                        newSet.delete(sectionKey);
                                                                    }
                                                                    setExpandedTranscriptSections(newSet);
                                                                }}
                                                            >
                                                                {intervalSegments.map((s: any, i: any) => (
                                                                    <TranscriptSegment
                                                                        timeMoment={momentSelectedToTrack+""}
                                                                        key={`${sectionKey}-${i}`}
                                                                        name={speakerLabels[s.SegmentSpeaker]}
                                                                        allSegments={s?.WordConfidence || []}
                                                                        segmentStart={s.SegmentStartTime}
                                                                        text={s.DisplayText}
                                                                        onClick={setAudioCurrentTime}
                                                                        highlightLocations={[
                                                                        ...s.EntitiesDetected.map((e: any) => ({
                                                                            start: e.BeginOffset,
                                                                            end: e.EndOffset,
                                                                            fn: (match: any, key: any, start: any, end: any, offsetStart: any, offsetEnd: any) => (
                                                                            <TranscriptOverlay
                                                                                key={key}
                                                                                colour={{}}
                                                                                visuallyHidden={`Entity - ${e.Type}`}
                                                                                data-start={start}
                                                                                data-end={end}
                                                                                data-offset-start={offsetStart}
                                                                                data-offset-end={offsetEnd}
                                                                                content={match}
                                                                                type={""}
                                                                                entityOffsetStart={e.BeginOffset}
                                                                                entityOffsetEnd={e.EndOffset}
                                                                                entityClass={"text-danger"}
                                                                                addType={offsetStart === e.BeginOffset ? true : false}
                                                                            >
                                                                            </TranscriptOverlay>
                                                                            ),
                                                                        })),
                                                                        ...(s.IssuesDetected? s.IssuesDetected?.map((issue: any) => ({
                                                                            start: issue.BeginOffset,
                                                                            end: issue.EndOffset,
                                                                            fn: (match: any, key: any, start: any, end: any, offsetStart: any, offsetEnd: any) => (
                                                                            <TranscriptOverlay
                                                                                key={key}
                                                                                colour="#ffff00"
                                                                                tooltip="Issue"
                                                                                data-start={start}
                                                                                data-end={end}
                                                                                data-offset-start={offsetStart}
                                                                                data-offset-end={offsetEnd}
                                                                                content={match}
                                                                                type={"Issue"}
                                                                                entityOffsetStart={issue.BeginOffset}
                                                                                entityOffsetEnd={issue.EndOffset}
                                                                                entityClass={"text-danger"}
                                                                                addType={offsetStart === issue.BeginOffset ? true : false}
                                                                            >
                                                                            </TranscriptOverlay>
                                                                            ),
                                                                        })) : []),
                                                                        ...(s.ActionItemsDetected? s.ActionItemsDetected?.map((issue: any) => ({
                                                                            start: issue.BeginOffset,
                                                                            end: issue.EndOffset,
                                                                            fn: (match: any, key: any, start: any, end: any, offsetStart: any, offsetEnd: any) => (
                                                                            <TranscriptOverlay
                                                                                key={key}
                                                                                colour="lightpink"
                                                                                tooltip="Action Item"
                                                                                data-start={start}
                                                                                data-end={end}
                                                                                data-offset-start={offsetStart}
                                                                                data-offset-end={offsetEnd}
                                                                                content={match}
                                                                                type={"Action Item"}
                                                                                entityOffsetStart={issue.BeginOffset}
                                                                                entityOffsetEnd={issue.EndOffset}
                                                                                entityClass={"text-danger"}
                                                                                addType={offsetStart === issue.BeginOffset ? true : false}
                                                                            >
                                                                            </TranscriptOverlay>
                                                                            ),
                                                                        })) : []),
                                                                        ...(s.OutcomesDetected? s.OutcomesDetected?.map((issue: any) => ({
                                                                            start: issue.BeginOffset,
                                                                            end: issue.EndOffset,
                                                                            fn: (match: any, key: any, start: any, end: any, offsetStart: any, offsetEnd: any) => (
                                                                            <TranscriptOverlay
                                                                                key={key}
                                                                                colour="aquamarine"
                                                                                tooltip="Outcome"
                                                                                data-start={start}
                                                                                data-end={end}
                                                                                data-offset-start={offsetStart}
                                                                                data-offset-end={offsetEnd}
                                                                                content={match}
                                                                                type={"Outcome"}
                                                                                entityOffsetStart={issue.BeginOffset}
                                                                                entityOffsetEnd={issue.EndOffset}
                                                                                entityClass={"text-danger"}
                                                                                addType={offsetStart === issue.BeginOffset ? true : false}
                                                                            >
                                                                            </TranscriptOverlay>
                                                                            ),
                                                                        })) : []),
                                                                        ]}
                                                                        score={s.SentimentIsPositive - s.SentimentIsNegative}
                                                                        interruption={s.SegmentInterruption}
                                                                        ivr={s?.IVRSegment || false}
                                                                        categoryList={s.CategoriesDetected}
                                                                    />
                                                                ))}
                                                            </ExpandableSection>
                                                        </Container>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })()
                            }
                            </div>
                            </>
                        }
                            </div>
                        </ExpandableSection>
                    </Container>
            </div>
            </ContentLayout>
        </div>
    );
};


export default AiSummaryPage;
